import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Sparkles, Calculator, Save, Download, TrendingUp, TrendingDown, ChevronDown, ChevronUp, FileText, Loader2, AlertCircle, ChefHat, Info, ArrowRight, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ThemeToggle from '../components/ThemeToggle';
import ToolSelector from '../components/ToolSelector';
import AdDisplay from '../components/AdDisplay';
import CalculatorTutorial from '../components/CalculatorTutorial';
import PlanInformation from '../components/calculator/PlanInformation';
import TaxRates from '../components/calculator/TaxRates';
import BatchBasics from '../components/calculator/BatchBasics';
import RecipeIntegration from '../components/calculator/RecipeIntegration';
import DirectCosts from '../components/calculator/DirectCosts';
import OperationalCosts from '../components/calculator/OperationalCosts';
import CapitalCosts from '../components/calculator/CapitalCosts';
import ProductionPlanning from '../components/calculator/ProductionPlanning';
import MarketCompetition from '../components/calculator/MarketCompetition';
import ProFormaResults from '../components/calculator/ProFormaResults';
import ProFormaStatement from '../components/calculator/ProFormaStatement';
import BusinessResultsTab from '../components/calculator/BusinessResultsTab';
import AdPlacement from '../components/calculator/AdPlacement';
import AIEstimationDialog from '../components/calculator/AIEstimationDialog';
import AIBusinessSummary from '../components/calculator/AIBusinessSummary';
import AIButton from '../components/calculator/AIButton';
import FieldInfo from '../components/calculator/FieldInfo';
import { TRAY_SIZES } from '../components/calculator/constants';
import HeadlineMetric from '../components/HeadlineMetric';
import DataTable from '../components/DataTable';
import CalculatorStepRail from '../components/CalculatorStepRail';
import LiveInsightsPanel from '../components/calculator/LiveInsightsPanel';
import { DollarSign, Percent, PackageOpen, Package, Wand2 } from 'lucide-react';

const BusinessCalc = () => {
 const { API, user } = useContext(AuthContext);
 const navigate = useNavigate();

 // Form state
 const [formData, setFormData] = useState({
 plan_name: '',
 product_description: '',
 location: '',
 freeze_dryer_model: '',
 tray_weight_unit: 'lbs',
 currency: user?.country === 'Canada' ? 'CAD' : 'USD',
 unit_system: 'imperial',
 units_per_batch: '',
 selling_price: '',
 sales_tax_rate: '',
 income_tax_rate: '',
 // Unit-level cost inputs
 ingredient_cost_unit: '$/lb',
 ingredient_weight_unit: 'lbs',
 ingredient_cost_per_lb: '',
 total_ingredient_weight_lb: '',
 ingredient_cost: '',
 // Packaging breakdown
 packaging_bag_cost: '',
 packaging_label_cost: '',
 packaging_o2_cost: '',
 packaging_seal_cost: '',
 packaging_other_cost: '',
 packaging_cost_per_unit: '',
 packaging_cost: '',
 // Labor breakdown by phase
 labor_prep_hours: '',
 labor_finish_hours: '',
 labor_pack_hours: '',
 labor_hours: '',
 labor_rate: '',
 // Electricity
 electricity_rate_kwh: '',
 machine_power_kw: '1.5',
 electricity_cost_per_hour: '',
 cycle_time_hours: '',
 electricity_cost: '',
 // Consumables breakdown
 consumable_pump_oil: '',
 consumable_tray_liners: '',
 consumable_cleaning: '',
 consumable_other: '',
 consumables_cost: '',
 waste_loss: '',
 // Machine specs
 shelf_space_sq_ft: '',
 unit_footprint_sq_in: '',
 // OpEx - Operational Costs
 monthly_rent: '',
 monthly_utilities: '',
 monthly_water: '',
 monthly_gas: '',
 monthly_insurance: '',
 monthly_software: '',
 monthly_marketing: '',
 monthly_maintenance: '',
 monthly_supplies: '',
 monthly_professional_services: '',
 other_overhead: '',
 batches_per_month: '',
 // CapEx - Capital Expenditures
 machine_price: '',
 machine_lifespan_years: '',
 loan_amount: '',
 interest_rate: '',
 loan_term_months: '',
 workspace_upgrades: '',
 licensing_fees: '',
 initial_inventory: '',
 batches_per_week: ''
 });

 const [aiEstimatedFields, setAiEstimatedFields] = useState({});
 const [outputs, setOutputs] = useState(null);
 const [savedPlans, setSavedPlans] = useState([]);
 const [showOverhead, setShowOverhead] = useState(false);
 const [showEquipment, setShowEquipment] = useState(false);
 const [loading, setLoading] = useState({});
 const [sensitivity, setSensitivity] = useState(null);
 const [showSavePlanDialog, setShowSavePlanDialog] = useState(false);
 const [showLoadPlanDialog, setShowLoadPlanDialog] = useState(false);
 const [showCOGS, setShowCOGS] = useState(false);
 const [showProductionPlanning, setShowProductionPlanning] = useState(false);
 const [showMarketCompetition, setShowMarketCompetition] = useState(false);
 const [competitionData, setCompetitionData] = useState(null);
 const [competitionLoading, setCompetitionLoading] = useState(false);
 const [optimizing, setOptimizing] = useState(false);
 const [optimizationResults, setOptimizationResults] = useState(null);
 const [viewMode, setViewMode] = useState('desktop'); // 'desktop' or 'mobile'
 const [businessCalcStep, setBusinessCalcStep] = useState(0);
 const [productNotes, setProductNotes] = useState('');
 const [activeTab, setActiveTab] = useState('inputs'); // 'inputs' or 'results'
 const [aiBusinessSummary, setAiBusinessSummary] = useState(null);
 // When `loadExample` fires, we flip this to true so the AIBusinessSummary
 // component renders a canned snapshot instead of hitting the paid AI
 // endpoint. Cleared by "New Plan" and any manual AI trigger (so real
 // usage starts consuming credits again).
 const [isDemoPlan, setIsDemoPlan] = useState(false);
 const [aiDialogOpen, setAiDialogOpen] = useState(false);
 const [aiDialogField, setAiDialogField] = useState('');
 const [aiDialogLabel, setAiDialogLabel] = useState('');
 const [aiPrompt, setAiPrompt] = useState('');
 const [aiLoading, setAiLoading] = useState(false);
 const [aiCycleTimeEstimated, setAiCycleTimeEstimated] = useState(false);
 const [showUnitsCalculator, setShowUnitsCalculator] = useState(false);
 const [showPieceCostCalculator, setShowPieceCostCalculator] = useState(false);
 const [pieceCostCalc, setPieceCostCalc] = useState({
 piecesInBag: '',
 bagWeight: '',
 bagWeightUnit: 'lbs',
 costPerBag: '',
 costPerPiece: 0,
 costPerUnit: 0
 });
 const [weightCalculator, setWeightCalculator] = useState({
 raw_weight: '',
 product_type: 'fruit',
 unit_weight: '',
 water_content: 80
 });

 // Recipe Integration State
 const [recipes, setRecipes] = useState([]);
 const [selectedRecipeId, setSelectedRecipeId] = useState('');
 const [hasRecipeAccess, setHasRecipeAccess] = useState(false);
 const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
 const [checkingAccess, setCheckingAccess] = useState(true);

 // Collapsible sections — start closed so the initial view is compact.
 // Users open the section they actually need to work on next.
 const [showPlanInfo, setShowPlanInfo] = useState(true);
 const [showTaxRates, setShowTaxRates] = useState(false);
 const [showBatchBasics, setShowBatchBasics] = useState(false);
 
 // Export options
 const [showExportDialog, setShowExportDialog] = useState(false);
 const [exportOptions, setExportOptions] = useState({
 planInfo: true,
 taxRates: true,
 batchBasics: true,
 directCosts: true,
 opex: true,
 capex: true,
 proForma: true,
 productionPlanning: true, // Always included
 marginStrategy: true, // Always included
 sensitivity: true
 });

 // Enhanced Tray Calculator State
 const [selectedTraySize, setSelectedTraySize] = useState('');
 const [numTrays, setNumTrays] = useState('');
 const [calculationMethod, setCalculationMethod] = useState('pieces'); // 'pieces' or 'weight'
 const [weightPerTray, setWeightPerTray] = useState('');
 const [piecesPerTray, setPiecesPerTray] = useState('');
 const [piecesPerBag, setPiecesPerBag] = useState('');
 const [totalBatchWeight, setTotalBatchWeight] = useState(''); // Total weight for all pieces in batch
 
 // Enhanced calculation fields
 const [pieceSize, setPieceSize] = useState(''); // sq inches per piece
 const [cycleTimeHours, setCycleTimeHours] = useState('24'); // default 24 hours
 const [wetWeightPerTray, setWetWeightPerTray] = useState('');
 const [dryWeightPerTray, setDryWeightPerTray] = useState('');
 const [waterContentPercent, setWaterContentPercent] = useState('');
 const [unitType, setUnitType] = useState('pieces'); // 'pounds', 'grams', 'ounces', 'pieces'
 const [quantityPerUnit, setQuantityPerUnit] = useState(''); // How many lbs/grams/oz/pieces per unit

 useEffect(() => {
 // Scroll to top of page on mount
 window.scrollTo(0, 0);
 
 checkRecipeAccess();
 fetchRecipes();
 }, []);

 // Profile completion is now a soft nudge in the UserPortal, not a hard
 // gate on the Business Calculator. The old redirect blocked legit users
 // who were missing a single optional field (e.g. postal code) and had
 // no way to know which field was outstanding.

 // Load saved plans on mount
 useEffect(() => {
 fetchSavedPlans();
 }, []);

 // Auto-calculate Units per Batch when values change
 useEffect(() => {
 if (calculationMethod === 'pieces' && piecesPerTray && piecesPerBag && numTrays) {
 const totalPieces = parseFloat(piecesPerTray) * parseFloat(numTrays);
 const units = Math.floor(totalPieces / parseFloat(piecesPerBag));
 setFormData(prev => ({ ...prev, units_per_batch: units }));
 } else if (calculationMethod === 'weight' && dryWeightPerTray && quantityPerUnit && numTrays) {
 const totalDryWeight = parseFloat(dryWeightPerTray) * parseFloat(numTrays);
 const weightPerUnit = unitType === 'pounds' ? parseFloat(quantityPerUnit) : 
 unitType === 'ounces' ? parseFloat(quantityPerUnit) / 16 : 
 parseFloat(quantityPerUnit) / 453.592;
 const units = Math.floor(totalDryWeight / weightPerUnit);
 setFormData(prev => ({ ...prev, units_per_batch: units }));
 }
 }, [calculationMethod, piecesPerTray, piecesPerBag, numTrays, dryWeightPerTray, quantityPerUnit, unitType]);

 // Auto-update ingredient total weight and cost when batch basics or units change
 useEffect(() => {
 let totalWeight = 0;
 if (calculationMethod === 'weight' && wetWeightPerTray && numTrays) {
 totalWeight = parseFloat(wetWeightPerTray) * parseFloat(numTrays);
 } else if (calculationMethod === 'pieces' && totalBatchWeight) {
 // Use the user-provided total batch weight for pieces
 totalWeight = parseFloat(totalBatchWeight);
 }
 
 if (totalWeight > 0) {
 setFormData(prev => {
 const costPerUnit = parseFloat(prev.ingredient_cost_per_lb) || 0;
 
 // For weight calculation, convert units if they don't match
 let totalCost = 0;
 if (calculationMethod === 'weight' && costPerUnit > 0) {
 const costUnit = (prev.ingredient_cost_unit || '$/lb').replace('$/', '');
 const weightUnit = prev.ingredient_weight_unit || 'lbs';
 
 // Convert weight to match cost unit
 const convertedWeight = convertWeight(totalWeight, weightUnit, costUnit);
 totalCost = costPerUnit * convertedWeight;
 } else if (calculationMethod === 'pieces' && costPerUnit > 0) {
 // For pieces, use batch weight with unit conversion
 const costUnit = (prev.ingredient_cost_unit || '$/lb').replace('$/', '');
 const weightUnit = prev.ingredient_weight_unit || 'lbs';
 const convertedWeight = convertWeight(totalWeight, weightUnit, costUnit);
 totalCost = costPerUnit * convertedWeight;
 }
 
 return {
 ...prev,
 total_ingredient_weight_lb: totalWeight.toString(),
 ingredient_cost: costPerUnit > 0 ? totalCost.toFixed(2) : prev.ingredient_cost
 };
 });
 }
 }, [calculationMethod, wetWeightPerTray, totalBatchWeight, numTrays, formData.ingredient_cost_unit, formData.ingredient_weight_unit, formData.ingredient_cost_per_lb]);

 const fetchSavedPlans = async () => {
 try {
 const response = await axios.get(`${API}/calculator/plans`);
 setSavedPlans(response.data);
 } catch (error) {
 console.error('Failed to load plans:', error);
 }
 };

 const checkRecipeAccess = async () => {
 try {
 const response = await axios.get(`${API}/check-feature/recipe_engine_pro`);
 setHasRecipeAccess(response.data.has_access);
 } catch (error) {
 console.error('Failed to check recipe access:', error);
 setHasRecipeAccess(false);
 } finally {
 setCheckingAccess(false);
 }
 };

 const fetchRecipes = async () => {
 try {
 const response = await axios.get(`${API}/recipes/calculator-summary`);
 setRecipes(response.data);
 } catch (error) {
 console.error('Failed to load recipes:', error);
 }
 };

 const handleRecipeSelect = (recipeId) => {
 if (!hasRecipeAccess) {
 setShowUpgradeDialog(true);
 return;
 }

 setSelectedRecipeId(recipeId);
 
 const recipe = recipes.find(r => r.id === recipeId);
 if (!recipe) return;

 // Calculate total ingredient cost from recipe
 let totalIngredientCost = 0;
 recipe.ingredients.forEach(ingredient => {
 // Use a rough estimation: $0.50 per 100g as base (can be improved)
 const costPer100g = 0.50;
 const quantityIn100g = ingredient.quantity / 100;
 totalIngredientCost += quantityIn100g * costPer100g;
 });

 // Auto-populate ingredient cost
 setFormData({
 ...formData,
 ingredient_cost: totalIngredientCost.toFixed(2),
 product_description: recipe.description || recipe.name
 });

 // Mark as recipe-derived
 setAiEstimatedFields({
 ...aiEstimatedFields,
 ingredient_cost: {
 value: totalIngredientCost.toFixed(2),
 explanation: `Estimated from recipe "${recipe.name}" with ${recipe.ingredient_count} ingredients`,
 isFromRecipe: true
 }
 });

 toast.success(`Loaded ingredient costs from "${recipe.name}"`);
 };

 const openAIDialog = (fieldName, label) => {
 setAiDialogField(fieldName);
 setAiDialogLabel(label);
 setAiPrompt('');
 setAiDialogOpen(true);
 };

 const handleAIEstimate = async () => {
 if (!aiPrompt.trim()) {
 toast.error('Please enter your question');
 return;
 }

 setAiLoading(true);
 
 try {
 const response = await axios.post(`${API}/calculator/ai-estimate`, {
 field_name: aiDialogField,
 user_prompt: aiPrompt,
 use_web_search: true,
 context: {
 country: user?.country || 'United States',
 state: user?.state || '',
 currency: formData.currency || 'USD',
 unit_system: formData.unit_system || 'imperial',
 business_type: user?.primary_use || 'Home Use',
 product_type: 'General',
 machine_size: 'Medium'
 },
 form_values: formData
 });

 const { suggested_value, explanation } = response.data;
 
 setFormData({ ...formData, [aiDialogField]: suggested_value });
 setAiEstimatedFields({ 
 ...aiEstimatedFields, 
 [aiDialogField]: { value: suggested_value, explanation, prompt: aiPrompt } 
 });
 
 toast.success('AI estimate applied!');
 setAiDialogOpen(false);
 } catch (error) {
 toast.error('Failed to get AI estimate');
 } finally {
 setAiLoading(false);
 }
 };

 const calculateUnitsFromWeight = () => {
 const rawWeight = parseFloat(weightCalculator.raw_weight) || 0;
 const waterContent = parseFloat(weightCalculator.water_content) || 80;
 const unitWeight = parseFloat(weightCalculator.unit_weight) || 1;
 const isMetric = formData.unit_system === 'metric';
 
 if (rawWeight === 0 || unitWeight === 0) {
 toast.error('Please fill in all weight calculator fields');
 return;
 }

 // Calculate dry weight after freeze-drying
 const dryWeightPercent = (100 - waterContent) / 100;
 const dryWeight = rawWeight * dryWeightPercent;
 
 // Convert to smaller units (oz for imperial, g for metric)
 const dryWeightSmall = isMetric ? dryWeight * 1000 : dryWeight * 16;
 
 // Calculate number of units
 const units = Math.floor(dryWeightSmall / unitWeight);
 
 const weightUnit = isMetric ? 'kg' : 'lbs';
 setFormData({ ...formData, units_per_batch: units });
 setShowUnitsCalculator(false);
 toast.success(`Calculated ${units} units from ${rawWeight}${weightUnit} raw product (${waterContent}% water)`);
 };

 // Backend Pydantic model expects numeric fields; our Input elements
 // hand back strings. Coerce anything that looks like a number to
 // Number, blank -> null (Pydantic's Optional accepts null), and leave
 // real strings (plan_name, product_description, currency, unit_system,
 // location, freeze_dryer_model, tray_size, etc.) untouched.
 const KEEP_AS_STRING = new Set([
   'plan_name', 'product_description', 'currency', 'unit_system', 'location',
   'freeze_dryer_model', 'freeze_dryer_model_id', 'tray_size', 'business_type',
   'primary_use', 'notes',
 ]);
 const coerceFormForApi = (fd) => {
   const out = {};
   for (const [k, v] of Object.entries(fd || {})) {
     if (KEEP_AS_STRING.has(k)) { out[k] = v; continue; }
     if (v === '' || v === undefined) { out[k] = null; continue; }
     if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) {
       out[k] = Number(v);
     } else {
       out[k] = v;
     }
   }
   return out;
 };


 const calculateResults = () => {
 try {
 // Switch to results tab after calculation
 setActiveTab('results');
 // Scroll to top of page
 window.scrollTo({ top: 0, behavior: 'smooth' });
 } catch (error) {
 console.error('Error in calculateResults:', error);
 toast.error('Error calculating results: ' + error.message);
 return;
 }
 const units = parseFloat(formData.units_per_batch) || 0;
 const price = parseFloat(formData.selling_price) || 0;
 
 if (units === 0 || price === 0) {
 toast.error('Please fill in Units per Batch and Selling Price');
 return;
 }

 // Direct costs (COGS)
 const ingredientCost = parseFloat(formData.ingredient_cost) || 0;
 const packagingCost = parseFloat(formData.packaging_cost) || 0;
 const laborCost = (parseFloat(formData.labor_hours) || 0) * (parseFloat(formData.labor_rate) || 0);
 const electricityCost = parseFloat(formData.electricity_cost) || 0;
 const consumablesCost = parseFloat(formData.consumables_cost) || 0;
 const wasteLoss = parseFloat(formData.waste_loss) || 0;
 
 const totalDirectCost = ingredientCost + packagingCost + laborCost + electricityCost + consumablesCost + wasteLoss;

 // OpEx - Overhead per batch
 const monthlyRent = parseFloat(formData.monthly_rent) || 0;
 const monthlyUtilities = parseFloat(formData.monthly_utilities) || 0;
 const monthlyWater = parseFloat(formData.monthly_water) || 0;
 const monthlyGas = parseFloat(formData.monthly_gas) || 0;
 const monthlyInsurance = parseFloat(formData.monthly_insurance) || 0;
 const monthlySoftware = parseFloat(formData.monthly_software) || 0;
 const monthlyMarketing = parseFloat(formData.monthly_marketing) || 0;
 const monthlyMaintenance = parseFloat(formData.monthly_maintenance) || 0;
 const monthlySupplies = parseFloat(formData.monthly_supplies) || 0;
 const monthlyProfServices = parseFloat(formData.monthly_professional_services) || 0;
 const otherOverhead = parseFloat(formData.other_overhead) || 0;
 const batchesPerMonth = parseFloat(formData.batches_per_month) || 1;
 
 const totalMonthlyOverhead = monthlyRent + monthlyUtilities + monthlyWater + monthlyGas + 
 monthlyInsurance + monthlySoftware + monthlyMarketing + 
 monthlyMaintenance + monthlySupplies + monthlyProfServices + otherOverhead;
 const overheadPerBatch = totalMonthlyOverhead / batchesPerMonth;

 // CapEx - Capital Expenditures per batch
 // 1. Machine depreciation
 const machinePrice = parseFloat(formData.machine_price) || 0;
 const lifespanYears = parseFloat(formData.machine_lifespan_years) || 5;
 const batchesPerWeek = parseFloat(formData.batches_per_week) || 1;
 const totalBatches = lifespanYears * 52 * batchesPerWeek;
 const machineCostPerBatch = totalBatches > 0 ? machinePrice / totalBatches : 0;
 
 // 2. Loan payment per batch (if applicable)
 const loanAmount = parseFloat(formData.loan_amount) || 0;
 const interestRate = parseFloat(formData.interest_rate) || 0;
 const loanTermMonths = parseFloat(formData.loan_term_months) || 60;
 
 let monthlyLoanPayment = 0;
 if (loanAmount > 0 && interestRate > 0 && loanTermMonths > 0) {
 const monthlyRate = (interestRate / 100) / 12;
 monthlyLoanPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, loanTermMonths)) / 
 (Math.pow(1 + monthlyRate, loanTermMonths) - 1);
 }
 const loanPaymentPerBatch = monthlyLoanPayment / batchesPerMonth;
 
 // 3. Other CapEx amortized over 5 years
 const workspaceUpgrades = parseFloat(formData.workspace_upgrades) || 0;
 const licensingFees = parseFloat(formData.licensing_fees) || 0;
 const initialInventory = parseFloat(formData.initial_inventory) || 0;
 const otherCapExTotal = workspaceUpgrades + licensingFees + initialInventory;
 const otherCapExPerBatch = totalBatches > 0 ? otherCapExTotal / totalBatches : 0;
 
 const totalCapExPerBatch = machineCostPerBatch + loanPaymentPerBatch + otherCapExPerBatch;

 // Total cost per batch (before taxes)
 const totalCostPerBatch = totalDirectCost + overheadPerBatch + totalCapExPerBatch;
 const costPerUnit = totalCostPerBatch / units;
 
 // Revenue calculations
 const totalRevenue = units * price;
 const salesTaxRate = parseFloat(formData.sales_tax_rate) || 0;
 const salesTaxAmount = totalRevenue * (salesTaxRate / 100);
 const netRevenueAfterSalesTax = totalRevenue - salesTaxAmount;
 
 // Profit calculations
 const grossProfit = netRevenueAfterSalesTax - totalDirectCost;
 const grossMarginPercent = netRevenueAfterSalesTax > 0 ? (grossProfit / netRevenueAfterSalesTax) * 100 : 0;
 
 const profitBeforeTax = netRevenueAfterSalesTax - totalCostPerBatch;
 const incomeTaxRate = parseFloat(formData.income_tax_rate) || 0;
 const incomeTaxAmount = profitBeforeTax > 0 ? profitBeforeTax * (incomeTaxRate / 100) : 0;
 const netProfit = profitBeforeTax - incomeTaxAmount;
 const netMarginPercent = netRevenueAfterSalesTax > 0 ? (netProfit / netRevenueAfterSalesTax) * 100 : 0;
 
 // Per unit profit
 const profitPerUnit = netProfit / units;
 
 // Additional metrics
 const laborHours = parseFloat(formData.labor_hours) || 1;
 const profitPerHour = laborHours > 0 ? netProfit / laborHours : 0;
 const cycleTime = parseFloat(formData.cycle_time_hours) || 24;
 const profitPerMachineHour = cycleTime > 0 ? netProfit / cycleTime : 0;
 
 const breakEvenUnits = price > 0 ? totalCostPerBatch / price : 0;
 const breakEvenPrice = units > 0 ? totalCostPerBatch / units : 0;
 
 const monthlyProfit = netProfit * batchesPerMonth;
 const machinePaybackMonths = monthlyProfit > 0 ? machinePrice / monthlyProfit : 0;

 const results = {
 // Direct costs
 total_direct_cost: totalDirectCost.toFixed(2),
 ingredient_cost: ingredientCost.toFixed(2),
 packaging_cost: packagingCost.toFixed(2),
 labor_cost: laborCost.toFixed(2),
 electricity_cost: electricityCost.toFixed(2),
 consumables_cost: consumablesCost.toFixed(2),
 waste_loss: wasteLoss.toFixed(2),
 
 // OpEx
 overhead_per_batch: overheadPerBatch.toFixed(2),
 total_monthly_overhead: totalMonthlyOverhead.toFixed(2),
 
 // CapEx
 capex_per_batch: totalCapExPerBatch.toFixed(2),
 machine_cost_per_batch: machineCostPerBatch.toFixed(2),
 loan_payment_per_batch: loanPaymentPerBatch.toFixed(2),
 other_capex_per_batch: otherCapExPerBatch.toFixed(2),
 
 // Totals
 total_cost_per_batch: totalCostPerBatch.toFixed(2),
 cost_per_unit: costPerUnit.toFixed(2),
 
 // Revenue
 total_revenue: totalRevenue.toFixed(2),
 sales_tax_amount: salesTaxAmount.toFixed(2),
 net_revenue_after_sales_tax: netRevenueAfterSalesTax.toFixed(2),
 
 // Profitability
 gross_profit: grossProfit.toFixed(2),
 gross_margin_percent: grossMarginPercent.toFixed(1),
 profit_before_tax: profitBeforeTax.toFixed(2),
 income_tax_amount: incomeTaxAmount.toFixed(2),
 net_profit: netProfit.toFixed(2),
 net_margin_percent: netMarginPercent.toFixed(1),
 profit_per_unit: profitPerUnit.toFixed(2),
 profit_per_hour: profitPerHour.toFixed(2),
 profit_per_machine_hour: profitPerMachineHour.toFixed(2),
 
 // Break-even
 breakeven_units: breakEvenUnits.toFixed(1),
 breakeven_price: breakEvenPrice.toFixed(2),
 
 // Long-term
 monthly_profit: monthlyProfit.toFixed(2),
 machine_payback_months: machinePaybackMonths.toFixed(1)
 };

 setOutputs(results);
 toast.success('Calculations complete!');
 
 // Automatically run market competition analysis if product and location provided
 if (formData.product_description && formData.location) {
 analyzeMarketCompetition();
 }
 
 // Automatically calculate sensitivity analysis
 setTimeout(() => {
 calculateSensitivityAuto(results);
 }, 100);
 };
 
 const analyzeMarketCompetition = async () => {
 if (!formData.product_description || !formData.location) {
 toast.error('Please enter Product Type and Location first');
 return;
 }
 
 setCompetitionLoading(true);
 try {
 const response = await axios.post(`${API}/calculator/market-competition`, {
 product_type: formData.product_description,
 location: formData.location
 });
 // Guard against pydantic-error bodies that sneak through as 200s or
 // strange non-analysis shapes — the render tree explodes on
 // `{type, loc, msg}` objects. Only keep the two fields we display.
 const data = response.data || {};
 const analysisStr = typeof data.analysis === 'string' ? data.analysis : '';
 const diff = Number(data.difficulty_index);
 setCompetitionData({
   difficulty_index: Number.isFinite(diff) ? diff : 50,
   analysis: analysisStr || `Market competition analysis for ${formData.product_description} in ${formData.location}.`,
 });
 toast.success('Market competition analysis updated');
 } catch (error) {
 console.error('Market analysis error:', error);
 // Set fallback data if API fails
 setCompetitionData({
 difficulty_index: 50,
 analysis: `Market competition analysis unavailable. Estimated moderate competition for ${formData.product_description} in ${formData.location}. Conduct local market research for accurate data.`
 });
 toast.error('Using fallback market analysis data');
 } finally {
 setCompetitionLoading(false);
 }
 };

 const calculateSensitivity = async () => {
 if (!outputs) {
 toast.error('Please calculate results first');
 return;
 }
 
 try {
 const response = await axios.post(`${API}/calculator/sensitivity`, {
 plan_name: formData.plan_name,
 ...coerceFormForApi(formData),
 ai_estimated_fields: aiEstimatedFields
 });
 setSensitivity(response.data);
 toast.success('Sensitivity analysis complete');
 } catch (error) {
 console.error('Sensitivity error:', error.response?.data || error.message);
 // FastAPI 422 returns `detail` as an ARRAY of error objects with keys
 // {type, loc, msg, input, url}. Passing that raw to toast/JSX crashes
 // React with "Objects are not valid as a React child". Coerce to text.
 const d = error.response?.data?.detail;
 const msg = Array.isArray(d)
   ? d.map((e) => e?.msg || JSON.stringify(e)).join('; ')
   : (typeof d === 'string' ? d : 'Failed to calculate sensitivity analysis');
 toast.error(msg);
 }
 };

 const calculateSensitivityAuto = async (results) => {
 if (!results) {
 return;
 }
 
 try {
 const response = await axios.post(`${API}/calculator/sensitivity`, {
 plan_name: formData.plan_name,
 ...coerceFormForApi(formData),
 ai_estimated_fields: aiEstimatedFields
 });
 setSensitivity(response.data);
 } catch (error) {
 console.error('Auto-sensitivity error:', error.response?.data || error.message);
 }
 };

 const savePlan = async () => {
 try {
 const dataToSave = {
 plan_name: formData.plan_name,
 inputs: {
 ...formData,
 ai_estimated_fields: aiEstimatedFields,
 // Save additional UI state for Batch Basics
 selectedTraySize: selectedTraySize,
 numTrays: numTrays,
 calculationMethod: calculationMethod,
 pieceSize: pieceSize,
 piecesPerTray: piecesPerTray,
 piecesPerBag: piecesPerBag,
 totalBatchWeight: totalBatchWeight,
 waterContentPercent: waterContentPercent,
 wetWeightPerTray: wetWeightPerTray,
 dryWeightPerTray: dryWeightPerTray,
 weightPerTray: weightPerTray,
 quantityPerUnit: quantityPerUnit,
 unitType: unitType,
 cycleTimeHours: cycleTimeHours,
 // Freeze dryer model specs already in formData from BatchBasics:
 // freeze_dryer_model_id, freeze_dryer_model, max_batch_weight_lb, 
 // max_batch_weight_kg, tray_size, total_tray_area_sqin
 // Save collapsible section states
 showCOGS: showCOGS,
 showOverhead: showOverhead,
 showEquipment: showEquipment,
 showPlanInfo: showPlanInfo,
 showTaxRates: showTaxRates,
 showBatchBasics: showBatchBasics,
 showProductionPlanning: showProductionPlanning,
 productNotes: productNotes
 },
 outputs: outputs,
 // Add model details separately for easy access
 freeze_dryer_model: {
 id: formData.freeze_dryer_model_id,
 name: formData.freeze_dryer_model,
 trays: numTrays,
 tray_size: formData.tray_size,
 max_capacity_lb: formData.max_batch_weight_lb,
 max_capacity_kg: formData.max_batch_weight_kg,
 total_tray_area: formData.total_tray_area_sqin
 }
 };
 
 console.log('SAVING PLAN - formData:', formData);
 console.log('SAVING PLAN - Full data:', dataToSave);
 
 await axios.post(`${API}/calculator/plans`, dataToSave);
 toast.success('Plan saved successfully!');
 setShowSavePlanDialog(false);
 fetchSavedPlans();
 } catch (error) {
 toast.error('Failed to save plan');
 console.error('Save error:', error);
 }
 };

 const deletePlan = async (planId, planName, event) => {
 event.stopPropagation(); // Prevent triggering loadPlan
 
 if (!window.confirm(`Delete "${planName}"? This cannot be undone.`)) {
 return;
 }
 
 try {
 await axios.delete(`${API}/calculator/plans/${planId}`);
 toast.success(`Deleted: ${planName}`);
 fetchSavedPlans();
 } catch (error) {
 toast.error('Failed to delete plan');
 console.error('Delete error:', error);
 }
 };

 const loadPlan = (plan) => {
 console.log('LOADING PLAN - Raw plan data:', plan);
 console.log('LOADING PLAN - plan.inputs:', plan.inputs);
 
 // Restore formData directly (it includes all form fields)
 // Note: plan.inputs has BOTH formData fields AND UI state variables mixed together
 // We need to extract the UI state separately and set formData with the rest
 
 const savedInputs = plan.inputs;
 
 // Create a clean formData object by copying savedInputs and removing UI state variables
 const uiStateKeys = [
 'selectedTraySize', 'numTrays', 'calculationMethod', 'pieceSize',
 'piecesPerTray', 'piecesPerBag', 'totalBatchWeight', 'waterContentPercent',
 'wetWeightPerTray', 'dryWeightPerTray', 'weightPerTray', 'quantityPerUnit',
 'unitType', 'showCOGS', 'showOverhead', 'showEquipment', 'showPlanInfo',
 'showTaxRates', 'showBatchBasics', 'showProductionPlanning', 'productNotes',
 'ai_estimated_fields'
 ];
 
 const cleanFormData = {};
 Object.keys(savedInputs).forEach(key => {
 if (!uiStateKeys.includes(key)) {
 cleanFormData[key] = savedInputs[key];
 }
 });
 
 // Restore formData with only actual form fields
 console.log('LOADING PLAN - cleanFormData to restore:', cleanFormData);
 setFormData(cleanFormData);
 setAiEstimatedFields(savedInputs.ai_estimated_fields || {});
 setOutputs(plan.outputs);
 
 // Restore additional UI state for Batch Basics
 if (plan.inputs.selectedTraySize !== undefined) setSelectedTraySize(plan.inputs.selectedTraySize);
 if (plan.inputs.numTrays !== undefined) setNumTrays(plan.inputs.numTrays);
 if (plan.inputs.calculationMethod !== undefined) setCalculationMethod(plan.inputs.calculationMethod);
 if (plan.inputs.pieceSize !== undefined) setPieceSize(plan.inputs.pieceSize);
 if (plan.inputs.piecesPerTray !== undefined) setPiecesPerTray(plan.inputs.piecesPerTray);
 if (plan.inputs.piecesPerBag !== undefined) setPiecesPerBag(plan.inputs.piecesPerBag);
 if (plan.inputs.totalBatchWeight !== undefined) setTotalBatchWeight(plan.inputs.totalBatchWeight);
 if (plan.inputs.waterContentPercent !== undefined) setWaterContentPercent(plan.inputs.waterContentPercent);
 if (plan.inputs.wetWeightPerTray !== undefined) setWetWeightPerTray(plan.inputs.wetWeightPerTray);
 if (plan.inputs.dryWeightPerTray !== undefined) setDryWeightPerTray(plan.inputs.dryWeightPerTray);
 if (plan.inputs.weightPerTray !== undefined) setWeightPerTray(plan.inputs.weightPerTray);
 if (plan.inputs.quantityPerUnit !== undefined) setQuantityPerUnit(plan.inputs.quantityPerUnit);
 if (plan.inputs.unitType !== undefined) setUnitType(plan.inputs.unitType);
 if (plan.inputs.cycleTimeHours !== undefined) setCycleTimeHours(plan.inputs.cycleTimeHours);
 
 // Restore collapsible section states
 if (plan.inputs.showCOGS !== undefined) setShowCOGS(plan.inputs.showCOGS);
 if (plan.inputs.showOverhead !== undefined) setShowOverhead(plan.inputs.showOverhead);
 if (plan.inputs.showEquipment !== undefined) setShowEquipment(plan.inputs.showEquipment);
 if (plan.inputs.showPlanInfo !== undefined) setShowPlanInfo(plan.inputs.showPlanInfo);
 if (plan.inputs.showTaxRates !== undefined) setShowTaxRates(plan.inputs.showTaxRates);
 if (plan.inputs.showBatchBasics !== undefined) setShowBatchBasics(plan.inputs.showBatchBasics);
 if (plan.inputs.showProductionPlanning !== undefined) setShowProductionPlanning(plan.inputs.showProductionPlanning);
 if (plan.inputs.productNotes !== undefined) setProductNotes(plan.inputs.productNotes);
 
 setShowLoadPlanDialog(false);
 toast.success(`Loaded: ${plan.plan_name}`);
 };

 const createNewPlan = () => {
 // Reset all form data to initial state
 setFormData({
 plan_name: '',
 product_description: '',
 location: '',
 freeze_dryer_model: '',
 tray_weight_unit: 'lbs',
 currency: user?.country === 'Canada' ? 'CAD' : 'USD',
 unit_system: 'imperial',
 units_per_batch: '',
 selling_price: '',
 sales_tax_rate: '',
 income_tax_rate: '',
 ingredient_cost_unit: '$/lb',
 ingredient_weight_unit: 'lbs',
 ingredient_cost_per_lb: '',
 total_ingredient_weight_lb: '',
 ingredient_cost: '',
 packaging_bag_cost: '',
 packaging_label_cost: '',
 packaging_o2_cost: '',
 packaging_seal_cost: '',
 packaging_other_cost: '',
 packaging_cost_per_unit: '',
 packaging_cost: '',
 labor_prep_hours: '',
 labor_finish_hours: '',
 labor_pack_hours: '',
 labor_hours: '',
 labor_rate: '',
 electricity_rate_kwh: '',
 machine_power_kw: '1.5',
 electricity_cost_per_hour: '',
 cycle_time_hours: '',
 electricity_cost: '',
 consumable_pump_oil: '',
 consumable_tray_liners: '',
 consumable_cleaning: '',
 consumable_other: '',
 consumables_cost: '',
 waste_loss: '',
 shelf_space_sq_ft: '',
 unit_footprint_sq_in: '',
 monthly_rent: '',
 monthly_utilities: '',
 monthly_water: '',
 monthly_gas: '',
 monthly_insurance: '',
 monthly_software: '',
 monthly_marketing: '',
 monthly_maintenance: '',
 monthly_supplies: '',
 monthly_professional_services: '',
 other_overhead: '',
 batches_per_month: '',
 machine_price: '',
 machine_lifespan_years: '',
 loan_amount: '',
 interest_rate: '',
 loan_term_months: '',
 workspace_upgrades: '',
 licensing_fees: '',
 initial_inventory: '',
 batches_per_week: ''
 });
 
 // Reset other related states
 setQuantityPerUnit(1);
 setUnitType('unit');
 setProductNotes('');
 setOutputs(null);
 setSensitivity(null);
 setCompetitionData(null);
 setOptimizationResults(null);
 setAiEstimatedFields({});
 setAiBusinessSummary(null);
setIsDemoPlan(false);
 setActiveTab('inputs');
 
 toast.success('New plan created. All fields cleared.');
 };

 // Realistic demo: freeze-dried strawberries, small home-business scale.
 // Numbers chosen so the plan lands with a healthy positive margin
 // and a break-even inside 12 months — good "wow" moment for first-time users.
 const loadExample = () => {
 setFormData({
 plan_name: 'Freeze-Dried Strawberries (Demo)',
 product_description: 'Retail-ready 1oz mylar pouches of freeze-dried Grade-A strawberries, sold at farmers markets and via Shopify.',
 location: 'Bozeman, Montana, USA',
 freeze_dryer_model: 'Medium Freeze Dryer',
 tray_weight_unit: 'lbs',
 currency: (formData.currency || 'USD'),
 unit_system: 'imperial',
 units_per_batch: '40',
 selling_price: '12.50',
 sales_tax_rate: '',
 income_tax_rate: '',
 ingredient_cost_unit: '$/lb',
 ingredient_weight_unit: 'lbs',
 ingredient_cost_per_lb: '3.25',
 total_ingredient_weight_lb: '20',
 ingredient_cost: '65.00',
 packaging_bag_cost: '0.55',
 packaging_label_cost: '0.10',
 packaging_o2_cost: '0.18',
 packaging_seal_cost: '0.05',
 packaging_other_cost: '0.02',
 packaging_cost_per_unit: '0.90',
 packaging_cost: '36.00',
 labor_prep_hours: '1.5',
 labor_finish_hours: '1',
 labor_pack_hours: '1.5',
 labor_hours: '4',
 labor_rate: '18',
 electricity_rate_kwh: '0.14',
 machine_power_kw: '1.5',
 electricity_cost_per_hour: '',
 cycle_time_hours: '26',
 electricity_cost: '5.46',
 consumable_pump_oil: '2.00',
 consumable_tray_liners: '1.50',
 consumable_cleaning: '0.75',
 consumable_other: '0.25',
 consumables_cost: '4.50',
 waste_loss: '5',
 shelf_space_sq_ft: '',
 unit_footprint_sq_in: '',
 monthly_rent: '150',
 monthly_utilities: '80',
 monthly_water: '20',
 monthly_gas: '0',
 monthly_insurance: '35',
 monthly_software: '25',
 monthly_marketing: '60',
 monthly_maintenance: '15',
 monthly_supplies: '20',
 monthly_professional_services: '30',
 other_overhead: '10',
 batches_per_month: '10',
 machine_price: '3300',
 machine_lifespan_years: '10',
 loan_amount: '0',
 interest_rate: '0',
 loan_term_months: '0',
 workspace_upgrades: '250',
 licensing_fees: '75',
 initial_inventory: '400',
 batches_per_week: '2.5',
 });
 setOutputs(null);
 setSensitivity(null);
 setCompetitionData(null);
// Mark this as a demo plan so AIBusinessSummary renders the pre-baked
// snapshot instead of hitting the paid AI endpoint. Pre-fill the
// summary state directly so the analysis appears instantly after
// Calculate — no waiting spinner, no API hit, no credit debit.
setIsDemoPlan(true);
setAiBusinessSummary({
overall_assessment:
"This is a strong starter plan. At 40 pouches per batch, a $12.50 retail price, and a solid 65-70% gross margin, the demo shows a small home-business freeze-dry line that can pay back its equipment within 8-10 months at 10 batches/month. Farmers-market pricing and Shopify DTC keep customer acquisition costs low while the mylar-pouch shelf-life advantage sustains repeat orders. Focus your next steps on securing steady strawberry sourcing under $3.25/lb and stacking a second sales channel to smooth summer-only farmers-market income.",
strengths: [
"Gross margin around 65% comfortably absorbs seasonal ingredient price swings.",
"Break-even inside 12 months means the machine pays for itself before the second harvest season.",
"Retail-ready 1oz pouches carry the highest $/lb yield of any freeze-dry SKU.",
"Dual-channel plan (markets + Shopify) hedges against slow weekends.",
],
areas_for_improvement: [
"Marketing spend at $60/month is thin — plan for $150-200 once Shopify launches.",
"No sales tax collected — verify Montana rules once revenue crosses $100k/year.",
"Insurance at $35/month is low for retail food sales; check that policy covers liability at markets.",
"Batches per month cap of 10 leaves the medium dryer under-utilised — try scaling to 14-16 by tightening cycle turnaround.",
],
recommendations: [
"Lock in a June-October strawberry contract at $3.00-$3.25/lb with a local farm to protect margin.",
"Launch a 6-pouch subscription box on Shopify at $65/mo to convert market shoppers into recurring revenue.",
"Add a second SKU (freeze-dried blueberries or raspberries) to double market booth revenue without extra booth fees.",
"Track cost-per-batch weekly for the first 90 days — variance above 10% signals sourcing or labor drift.",
],
is_demo: true,
});
 setActiveTab('inputs');
 toast.success('Example plan loaded — scroll down to review the numbers, then hit Calculate.');
 setTimeout(() => {
 document.getElementById('section-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
 }, 100);
 };

 const exportPDF = () => {
 if (!outputs) {
 toast.error('Please calculate results first');
 return;
 }

 // Create PDF content as HTML
 const pdfContent = `
 <!DOCTYPE html>
 <html>
 <head>
 <style>
 body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
 h1 { color: #0EA5E9; border-bottom: 3px solid #0EA5E9; padding-bottom: 10px; }
 h2 { color: #0284C7; margin-top: 30px; border-bottom: 2px solid #E2E8F0; padding-bottom: 5px; }
 h3 { color: #475569; margin-top: 20px; }
 table { width: 100%; border-collapse: collapse; margin: 20px 0; }
 th, td { padding: 12px; text-align: left; border-bottom: 1px solid #E2E8F0; }
 th { background-color: #F1F5F9; font-weight: bold; }
 .section { margin-bottom: 30px; }
 .positive { color: #10B981; font-weight: bold; }
 .negative { color: #EF4444; font-weight: bold; }
 .highlight { background-color: #FEF3C7; padding: 15px; border-left: 4px solid #F59E0B; margin: 20px 0; }
 .bank-summary { background-color: #DBEAFE; padding: 20px; border-radius: 8px; margin-top: 30px; }
 .ai-estimate { color: #3B82F6; font-style: italic; font-size: 0.9em; }
 .market-comp { background-color: #F3E8FF; padding: 15px; border-radius: 8px; margin: 20px 0; }
 .production-plan { background-color: #E0F2FE; padding: 15px; border-radius: 8px; margin: 20px 0; }
 .margin-strategy { background-color: #FEF3C7; padding: 15px; border-radius: 8px; margin: 20px 0; }
 </style>
 </head>
 <body>
 <h1>Business Calculator Report</h1>
 <p><strong>Plan:</strong> ${formData.plan_name}</p>
 <p><strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>
 ${formData.product_description ? `<p><strong>Product:</strong> ${formData.product_description}</p>` : ''}
 ${formData.location ? `<p><strong>Location:</strong> ${formData.location}</p>` : ''}
 <p><strong>Unit System:</strong> ${formData.unit_system === 'metric' ? 'Metric (kg, g)' : 'Imperial (lbs, oz)'}</p>
 
 <div class="section">
 <h2>Batch Basics</h2>
 <table>
 <tr><td>Units per Batch</td><td>${formData.units_per_batch || 'N/A'}</td></tr>
 ${calculationMethod === 'pieces' && piecesPerBag && totalBatchWeight ? `
 <tr><td>Unit Size</td><td>${piecesPerBag} pieces (${(parseFloat(totalBatchWeight) / ((parseFloat(piecesPerTray) || 1) * (parseFloat(numTrays) || 1)) * parseFloat(piecesPerBag)).toFixed(1)}${formData.ingredient_weight_unit || 'lbs'}/unit)</td></tr>
 ` : calculationMethod === 'weight' && quantityPerUnit && unitType ? `
 <tr><td>Unit Size</td><td>${quantityPerUnit} ${unitType === 'ounces' ? 'oz' : unitType === 'pounds' ? 'lb' : unitType}/unit</td></tr>
 ` : ''}
 <tr><td>Selling Price per Unit</td><td>$${formData.selling_price || 'N/A'}</td></tr>
 </table>
 </div>

 <div class="section">
 <h2>Direct Costs (COGS)</h2>
 <table>
 <tr><td>Ingredient Cost</td><td>$${formData.ingredient_cost || '0.00'} ${aiEstimatedFields.ingredient_cost ? '<span class="ai-estimate">AI</span>' : ''}</td></tr>
 <tr><td>Packaging Cost</td><td>$${formData.packaging_cost || '0.00'} ${aiEstimatedFields.packaging_cost ? '<span class="ai-estimate">AI</span>' : ''}</td></tr>
 <tr><td>Labor Hours</td><td>${formData.labor_hours || '0'} hrs</td></tr>
 <tr><td>Labor Rate</td><td>$${formData.labor_rate || '0.00'}/hr ${aiEstimatedFields.labor_rate ? '<span class="ai-estimate">AI</span>' : ''}</td></tr>
 <tr><td>Electricity Cost</td><td>$${formData.electricity_cost || '0.00'} ${aiEstimatedFields.electricity_cost ? '<span class="ai-estimate">AI</span>' : ''}</td></tr>
 <tr><td>Consumables Cost</td><td>$${formData.consumables_cost || '0.00'}</td></tr>
 <tr><td>Waste/Loss</td><td>$${formData.waste_loss || '0.00'}</td></tr>
 </table>
 </div>

 <div class="section">
 <h2>Operational Costs (OpEx) - Monthly</h2>
 <table>
 <tr><td>Rent/Space</td><td>$${formData.monthly_rent || '0.00'}</td></tr>
 <tr><td>Utilities</td><td>$${formData.monthly_utilities || '0.00'}</td></tr>
 <tr><td>Insurance</td><td>$${formData.monthly_insurance || '0.00'}</td></tr>
 <tr><td>Software/Tools</td><td>$${formData.monthly_software || '0.00'}</td></tr>
 <tr><td>Marketing</td><td>$${formData.monthly_marketing || '0.00'}</td></tr>
 <tr><td>Maintenance</td><td>$${formData.monthly_maintenance || '0.00'}</td></tr>
 <tr><td>Other Overhead</td><td>$${formData.other_overhead || '0.00'}</td></tr>
 <tr><th>Expected Batches/Month</th><th>${formData.batches_per_month || 'N/A'}</th></tr>
 </table>
 </div>

 <div class="section">
 <h2>Capital Expenditures (CapEx)</h2>
 <table>
 <tr><td>Equipment Purchase</td><td>$${formData.machine_price || '0.00'}</td></tr>
 <tr><td>Workspace Upgrades</td><td>$${formData.workspace_upgrades || '0.00'}</td></tr>
 <tr><td>Initial Inventory</td><td>$${formData.initial_inventory || '0.00'}</td></tr>
 <tr><td>Licensing & Permits</td><td>$${formData.licensing_fees || '0.00'}</td></tr>
 ${formData.loan_amount ? `<tr><td>Loan Amount</td><td>$${formData.loan_amount} @ ${formData.interest_rate}% for ${formData.loan_term_months} months</td></tr>` : ''}
 <tr><td>Equipment Lifespan</td><td>${formData.machine_lifespan_years || '5'} years</td></tr>
 </table>
 </div>

 <div class="section">
 <h2>Financial Analysis</h2>
 <table>
 <tr><td>Total Direct Cost</td><td>$${outputs.total_direct_cost}</td></tr>
 <tr><td>Overhead per Batch</td><td>$${outputs.overhead_per_batch}</td></tr>
 <tr><td>Total Cost per Batch</td><td>$${outputs.total_cost_per_batch}</td></tr>
 <tr><td>Cost per Unit</td><td>$${outputs.cost_per_unit}</td></tr>
 <tr><th>Total Revenue</th><th class="positive">$${outputs.total_revenue}</th></tr>
 <tr><th>Gross Profit</th><th class="${parseFloat(outputs.gross_profit) >= 0 ? 'positive' : 'negative'}">$${outputs.gross_profit}</th></tr>
 <tr><td>Gross Margin</td><td>${outputs.gross_margin_percent}%</td></tr>
 <tr><th>Net Profit</th><th class="${parseFloat(outputs.net_profit) >= 0 ? 'positive' : 'negative'}">$${outputs.net_profit}</th></tr>
 <tr><th>Net Margin</th><th>${outputs.net_margin_percent}%</th></tr>
 <tr><td>Profit per Hour</td><td>$${outputs.profit_per_hour}</td></tr>
 <tr><td>Monthly Profit</td><td>$${outputs.monthly_profit}</td></tr>
 </table>
 </div>

 <div class="section">
 <h2>Break-Even Analysis</h2>
 <table>
 <tr><td>Break-even Units</td><td>${outputs.breakeven_units} units per batch</td></tr>
 <tr><td>Break-even Price</td><td>$${outputs.breakeven_price} per unit</td></tr>
 <tr><td>Machine Payback Period</td><td>${outputs.machine_payback_months} months</td></tr>
 </table>
 </div>

 ${sensitivity ? `
 <div class="section">
 <h2>Sensitivity Analysis</h2>
 <table>
 <tr><th>Scenario</th><th>Net Margin</th></tr>
 <tr><td>Base Case</td><td>${sensitivity.base_margin}%</td></tr>
 <tr><td>Ingredient Cost +10%</td><td>${sensitivity.ingredient_plus_10}%</td></tr>
 <tr><td>Ingredient Cost -10%</td><td>${sensitivity.ingredient_minus_10}%</td></tr>
 <tr><td>Selling Price +10%</td><td class="positive">${sensitivity.price_plus_10}%</td></tr>
 <tr><td>Selling Price -10%</td><td class="negative">${sensitivity.price_minus_10}%</td></tr>
 <tr><td>Labor Rate +20%</td><td>${sensitivity.labor_plus_20}%</td></tr>
 </table>
 </div>
 ` : ''}

 ${competitionData ? `
 <div class="section market-comp">
 <h2>🔍 Market Competition Analysis</h2>
 <p><strong>Product Type:</strong> ${formData.product_type || 'N/A'}</p>
 <p><strong>Location:</strong> ${formData.location || 'N/A'}</p>
 <p><strong>Competition Difficulty Index:</strong> ${competitionData.difficulty_index}% ${competitionData.difficulty_index > 70 ? '(High Competition)' : competitionData.difficulty_index > 40 ? '(Moderate Competition)' : '(Low Competition)'}</p>
 <p style="margin-top: 10px;"><em>${competitionData.analysis}</em></p>
 
 <h3>Realistic Sales Projections</h3>
 <table>
 <tr><th>Scenario</th><th>Capacity Used</th><th>Units/Month</th><th>Revenue/Month</th></tr>
 ${(() => {
 const difficulty = competitionData.difficulty_index;
 
 // Smart capacity calculation based on competition
 let pessimisticMultiplier, expectedMultiplier, optimisticMultiplier;
 
 if (difficulty < 50) {
 pessimisticMultiplier = 0.45;
 expectedMultiplier = 0.65;
 optimisticMultiplier = 0.90;
 } else {
 const competitionFactor = (difficulty - 50) / 50;
 pessimisticMultiplier = Math.max(0.20, 0.45 - (competitionFactor * 0.15));
 expectedMultiplier = Math.max(0.50, 0.65 - (competitionFactor * 0.15));
 optimisticMultiplier = Math.max(0.80, 0.90 - (competitionFactor * 0.10));
 }
 
 const unitsPerMonth = parseInt(formData.units_per_batch || 0) * parseInt(formData.batches_per_month || 0);
 const revenuePerMonth = unitsPerMonth * parseFloat(formData.selling_price || 0);
 
 // Calculate proper breakeven accounting for fixed and variable costs
 const pricePerUnit = parseFloat(formData.selling_price) || 0;
 const cogsPerUnit = parseFloat(outputs.total_direct_cost) / parseFloat(formData.units_per_batch || 1);
 const opexPerBatch = parseFloat(outputs.overhead_per_batch) || 0;
 const capexPerBatch = parseFloat(outputs.capex_per_batch) || 0;
 const unitsPerBatch = parseFloat(formData.units_per_batch) || 1;
 const batchesPerMonth = parseFloat(formData.batches_per_month) || 1;
 
 // Fixed costs per month (OpEx + CapEx for all batches)
 const fixedCostsPerMonth = (opexPerBatch + capexPerBatch) * batchesPerMonth;
 
 // Contribution margin per unit (Price - Variable Cost)
 const contributionMargin = pricePerUnit - cogsPerUnit;
 
 // Breakeven units = Fixed Costs / Contribution Margin
 const breakEvenUnits = contributionMargin > 0 ? Math.ceil(fixedCostsPerMonth / contributionMargin) : 0;
 const breakEvenPercentage = ((breakEvenUnits / unitsPerMonth) * 100).toFixed(0);
 
 return `
 <tr style="background-color: #F1F5F9; font-weight: bold;">
 <td>Breakeven</td>
 <td>${breakEvenPercentage}%</td>
 <td>${breakEvenUnits.toLocaleString()}</td>
 <td>$${(breakEvenUnits * pricePerUnit).toFixed(0)}</td>
 </tr>
 <tr>
 <td>Pessimistic</td>
 <td>${(pessimisticMultiplier * 100).toFixed(0)}%</td>
 <td>${Math.floor(unitsPerMonth * pessimisticMultiplier).toLocaleString()}</td>
 <td>$${(revenuePerMonth * pessimisticMultiplier).toFixed(0)}</td>
 </tr>
 <tr>
 <td>Expected</td>
 <td>${(expectedMultiplier * 100).toFixed(0)}%</td>
 <td>${Math.floor(unitsPerMonth * expectedMultiplier).toLocaleString()}</td>
 <td>$${(revenuePerMonth * expectedMultiplier).toFixed(0)}</td>
 </tr>
 <tr>
 <td>Optimistic</td>
 <td>${(optimisticMultiplier * 100).toFixed(0)}%</td>
 <td>${Math.floor(unitsPerMonth * optimisticMultiplier).toLocaleString()}</td>
 <td>$${(revenuePerMonth * optimisticMultiplier).toFixed(0)}</td>
 </tr>
 `;
 })()}
 </table>
 </div>
 ` : ''}

 ${outputs && outputs.cost_per_unit ? `
 <div class="section margin-strategy">
 <h2>💡 AI-Powered Margin Targeting Strategy</h2>
 <p>Alternative pricing strategies to achieve different margin targets:</p>
 
 ${calculationMethod === 'pieces' && piecesPerBag ? `
 <h3>Per Unit Pricing</h3>
 <table>
 <tr><th>Target Margin</th><th>Required Price/Unit</th><th>Price per Piece</th><th>Strategy</th></tr>
 <tr>
 <td>25%</td>
 <td>$${(parseFloat(outputs.cost_per_unit) / (1 - 0.25)).toFixed(2)}</td>
 <td>$${((parseFloat(outputs.cost_per_unit) / (1 - 0.25)) / parseFloat(piecesPerBag)).toFixed(3)}</td>
 <td>High Competition - Build Market Share</td>
 </tr>
 <tr style="background-color: #D1FAE5;">
 <td>45%</td>
 <td>$${(parseFloat(outputs.cost_per_unit) / (1 - 0.45)).toFixed(2)}</td>
 <td>$${((parseFloat(outputs.cost_per_unit) / (1 - 0.45)) / parseFloat(piecesPerBag)).toFixed(3)}</td>
 <td>⭐ Balanced Growth (Recommended)</td>
 </tr>
 <tr>
 <td>65%</td>
 <td>$${(parseFloat(outputs.cost_per_unit) / (1 - 0.65)).toFixed(2)}</td>
 <td>$${((parseFloat(outputs.cost_per_unit) / (1 - 0.65)) / parseFloat(piecesPerBag)).toFixed(3)}</td>
 <td>Low Competition - Premium Positioning</td>
 </tr>
 </table>
 <p style="font-size: 0.85em; margin-top: 8px;"><em>💡 ${piecesPerBag} pieces per unit - Price shown per piece helps with consumer comparison</em></p>
 ` : `
 <table>
 <tr><th>Target Margin</th><th>Required Price</th><th>Strategy</th></tr>
 <tr>
 <td>25%</td>
 <td>$${(parseFloat(outputs.cost_per_unit) / (1 - 0.25)).toFixed(2)}</td>
 <td>High Competition - Build Market Share</td>
 </tr>
 <tr style="background-color: #D1FAE5;">
 <td>45%</td>
 <td>$${(parseFloat(outputs.cost_per_unit) / (1 - 0.45)).toFixed(2)}</td>
 <td>⭐ Balanced Growth (Recommended)</td>
 </tr>
 <tr>
 <td>65%</td>
 <td>$${(parseFloat(outputs.cost_per_unit) / (1 - 0.65)).toFixed(2)}</td>
 <td>Low Competition - Premium Positioning</td>
 </tr>
 </table>
 `}
 <p style="font-size: 0.9em; margin-top: 10px;"><em>Note: Higher prices may reduce sales volume due to price elasticity.</em></p>
 </div>
 ` : ''}

 ${formData.cycle_time_hours && formData.units_per_batch && formData.batches_per_month ? `
 <div class="section production-plan">
 <h2>📊 Multi-Batch Production Planning</h2>
 <p><strong>Cycle Time:</strong> ${formData.cycle_time_hours} hours</p>
 <p><strong>Units per Batch:</strong> ${formData.units_per_batch}</p>
 <p><strong>Monthly Capacity:</strong> ${formData.batches_per_month} batches</p>
 
 ${parseFloat(formData.cycle_time_hours) <= 24 ? `
 <h3>Production Capacity by Work Schedule</h3>
 <table>
 <tr><th>Schedule</th><th>Daily Batches</th><th>Weekly Batches</th><th>Monthly Batches</th><th>Monthly Revenue</th></tr>
 ${(() => {
 const cycleHours = parseFloat(formData.cycle_time_hours);
 const unitsPerBatch = parseInt(formData.units_per_batch);
 const price = parseFloat(formData.selling_price);
 
 return [8, 12, 16, 24].map(hours => {
 const dailyBatches = Math.floor(hours / cycleHours);
 const weeklyBatches = dailyBatches * 7;
 const monthlyBatches = dailyBatches * 30;
 const monthlyRevenue = monthlyBatches * unitsPerBatch * price;
 
 return `
 <tr>
 <td>${hours}-hour days</td>
 <td>${dailyBatches}</td>
 <td>${weeklyBatches}</td>
 <td>${monthlyBatches}</td>
 <td>$${monthlyRevenue.toFixed(0)}</td>
 </tr>
 `;
 }).join('');
 })()}
 </table>
 ` : '<p><em>Extended cycle time limits daily batch production</em></p>'}
 </div>
 ` : ''}

 <div class="bank-summary">
 <h2>Bank/Lender Summary</h2>
 <h3>Investment Required</h3>
 <p><strong>Total Capital:</strong> $${(parseFloat(formData.machine_price || 0) + parseFloat(formData.workspace_upgrades || 0) + parseFloat(formData.initial_inventory || 0) + parseFloat(formData.licensing_fees || 0)).toFixed(2)}</p>
 
 <h3>Profitability Metrics</h3>
 <p><strong>Net Margin:</strong> ${outputs.net_margin_percent}%</p>
 <p><strong>Monthly Revenue:</strong> $${(parseFloat(outputs.total_revenue) * (parseFloat(formData.batches_per_month) || 1)).toFixed(2)}</p>
 <p><strong>Monthly Profit:</strong> $${outputs.monthly_profit}</p>
 
 <h3>Return on Investment</h3>
 <p><strong>Payback Period:</strong> ${outputs.machine_payback_months} months</p>
 <p><strong>Break-even Volume:</strong> ${outputs.breakeven_units} units per batch</p>
 
 <div class="highlight">
 <p><strong>Risk Assessment:</strong> ${parseFloat(outputs.net_margin_percent) >= 30 ? 'Low Risk - Strong margins' : parseFloat(outputs.net_margin_percent) >= 15 ? 'Moderate Risk - Acceptable margins' : 'Higher Risk - Tight margins'}</p>
 <p><strong>Recommendation:</strong> ${parseFloat(outputs.monthly_profit) > 0 ? 'Positive cash flow projection. Business model is viable.' : 'Negative cash flow. Consider cost reduction or price increase.'}</p>
 </div>
 </div>

 ${aiBusinessSummary ? `
 <div style="margin-top: 40px; padding: 20px; background-color: #F5F3FF; border: 2px solid #9333EA; border-radius: 8px;">
 <h2 style="color: #6B21A8; margin-top: 0; display: flex; align-items: center;">
 ✨ AI Business Recommendations
 </h2>
 
 <div style="margin-bottom: 20px;">
 <h3 style="color: #7C3AED; margin-bottom: 10px;">Overall Business Assessment</h3>
 <p style="font-size: 0.95em; line-height: 1.6; color: #4C1D95;">
 ${aiBusinessSummary.overall_assessment}
 </p>
 </div>

 ${aiBusinessSummary.strengths && aiBusinessSummary.strengths.length > 0 ? `
 <div style="margin-bottom: 20px; padding: 15px; background-color: #ECFDF5; border-left: 4px solid #10B981; border-radius: 4px;">
 <h3 style="color: #065F46; margin-top: 0; margin-bottom: 10px;">✓ Key Strengths</h3>
 <ul style="margin: 0; padding-left: 20px; color: #064E3B;">
 ${aiBusinessSummary.strengths.map(strength => `<li style="margin-bottom: 8px;">${strength}</li>`).join('')}
 </ul>
 </div>
 ` : ''}

 ${aiBusinessSummary.areas_for_improvement && aiBusinessSummary.areas_for_improvement.length > 0 ? `
 <div style="margin-bottom: 20px; padding: 15px; background-color: #FEF3C7; border-left: 4px solid #F59E0B; border-radius: 4px;">
 <h3 style="color: #92400E; margin-top: 0; margin-bottom: 10px;">⚠ Areas for Improvement</h3>
 <ul style="margin: 0; padding-left: 20px; color: #78350F;">
 ${aiBusinessSummary.areas_for_improvement.map(area => `<li style="margin-bottom: 8px;">${area}</li>`).join('')}
 </ul>
 </div>
 ` : ''}

 ${aiBusinessSummary.recommendations && aiBusinessSummary.recommendations.length > 0 ? `
 <div style="padding: 15px; background-color: #DBEAFE; border-left: 4px solid #3B82F6; border-radius: 4px;">
 <h3 style="color: #1E40AF; margin-top: 0; margin-bottom: 10px;">💡 Actionable Recommendations</h3>
 <ol style="margin: 0; padding-left: 20px; color: #1E3A8A;">
 ${aiBusinessSummary.recommendations.map(rec => `<li style="margin-bottom: 8px;">${rec}</li>`).join('')}
 </ol>
 </div>
 ` : ''}

 <p style="font-size: 0.8em; color: #6B7280; margin-top: 15px; font-style: italic;">
 <strong>Note:</strong> AI-generated recommendations are for informational purposes only. Consult with qualified professionals before making business decisions.
 </p>
 </div>
 ` : ''}

 ${productNotes ? `
 <div style="margin-top: 40px; padding: 20px; background-color: #EFF6FF; border: 2px solid #3B82F6; border-radius: 8px;">
 <h3 style="color: #1E40AF; margin-top: 0;">📝 Product Research Notes</h3>
 <p style="font-size: 0.9em; color: #1E3A8A; line-height: 1.6; white-space: pre-wrap;">
 ${productNotes}
 </p>
 </div>
 ` : ''}
 
 <div style="margin-top: 40px; padding: 20px; background-color: #FEF3C7; border: 2px solid #F59E0B; border-radius: 8px;">
 <h3 style="color: #92400E; margin-top: 0;">⚠️ LEGAL DISCLAIMER</h3>
 <p style="font-size: 0.85em; color: #78350F; line-height: 1.6;">
 <strong>For Educational & Exploratory Purposes Only.</strong> This financial analysis is provided for educational 
 and exploratory purposes only. All calculations, projections, and recommendations are hypothetical and based on 
 user-provided inputs. Freeze Dry Academy, Peak and Prairie Industries Corp., and their affiliates assume 
 <strong>NO LIABILITY</strong> for the accuracy, completeness, or suitability of these figures for any specific 
 business purpose. Users bear full responsibility for independently verifying all data, conducting their own research, 
 and consulting with qualified financial, legal, and business professionals before making any business decisions. 
 This tool is for business exploration and hypothetical testing only and does not constitute financial, investment, 
 tax, legal, or business advice.
 </p>
 </div>
 
 <p style="margin-top: 20px; text-align: center; color: #94A3B8; font-size: 0.85em;">
 Generated by Freeze Dry Academy Business Calculator<br>
 AI-Estimated values are marked with "AI"<br>
 Document Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
 </p>
 </body>
 </html>
 `;

 // Create a new window and print
 const printWindow = window.open('', '_blank');
 printWindow.document.write(pdfContent);
 printWindow.document.close();
 
 // Wait for content to load then trigger print
 setTimeout(() => {
 printWindow.print();
 }, 500);
 
 toast.success('PDF export opened in new window. Use Print to PDF.');
 };

 const optimizeWithAI = async () => {
 if (!formData.plan_name || !formData.selling_price) {
 toast.error('Please fill in Plan Name and Selling Price first');
 return;
 }

 setOptimizing(true);
 try {
 const response = await axios.post(`${API}/calculator/optimize`, {
 plan_name: formData.plan_name,
 product_description: formData.product_description,
 location: formData.location || `${user?.city}, ${user?.state}, ${user?.country}`,
 selling_price: parseFloat(formData.selling_price),
 current_data: formData
 });

 const { optimized_values, explanation } = response.data;
 
 // Store optimization results for display
 setOptimizationResults({
 current: {
 units_per_batch: formData.units_per_batch || 0,
 batches_per_month: formData.batches_per_month || 0,
 batches_per_week: formData.batches_per_week || 0
 },
 optimized: optimized_values,
 explanation: explanation
 });
 
 toast.success('AI Optimization Complete! Review the results below.');
 } catch (error) {
 toast.error('Failed to optimize. Try filling in more details first.');
 } finally {
 setOptimizing(false);
 }
 };

 const applyOptimization = () => {
 if (!optimizationResults) return;
 setFormData({ ...formData, ...optimizationResults.optimized });
 setOptimizationResults(null);
 toast.success('Optimized values applied!');
 };

 // TRAY_SIZES now imported from constants

 // Handle tray size selection
 const handleTraySelection = (trayName) => {
 setSelectedTraySize(trayName);
 if (numTrays && trayName && TRAY_SIZES[trayName]) {
 const tray = TRAY_SIZES[trayName];
 const areaSqIn = tray.width * tray.depth;
 const areaSqFt = areaSqIn / 144;
 const totalSqFt = areaSqFt * parseFloat(numTrays);
 setFormData(prev => ({ ...prev, shelf_space_sq_ft: totalSqFt.toFixed(2) }));
 }
 };

 const handleNumTrays = (value) => {
 setNumTrays(value);
 if (selectedTraySize && value && TRAY_SIZES[selectedTraySize]) {
 const tray = TRAY_SIZES[selectedTraySize];
 const areaSqIn = tray.width * tray.depth;
 const areaSqFt = areaSqIn / 144;
 const totalSqFt = areaSqFt * parseFloat(value);
 setFormData(prev => ({ ...prev, shelf_space_sq_ft: totalSqFt.toFixed(2) }));
 }
 };

 // Calculate units from weight or pieces per tray
 const calculateUnitsFromTray = () => {
 const trays = parseFloat(numTrays) || 0;
 
 if (trays === 0) {
 toast.error('Please enter number of trays');
 return;
 }

 if (calculationMethod === 'weight') {
 const weightPer = parseFloat(weightPerTray) || 0;
 const totalWeight = weightPer * trays;
 setFormData(prev => ({ ...prev, total_ingredient_weight_lb: totalWeight.toFixed(2) }));
 toast.success(`Total weight: ${totalWeight.toFixed(2)} lbs across ${trays} trays`);
 } else {
 const piecesPer = parseFloat(piecesPerTray) || 0;
 const perBag = parseFloat(piecesPerBag) || 1; // Default to 1 if not set
 const totalPieces = piecesPer * trays;
 const unitsPerBatch = Math.floor(totalPieces / perBag);
 
 if (piecesPerBag && parseFloat(piecesPerBag) > 0) {
 setFormData(prev => ({ ...prev, units_per_batch: unitsPerBatch }));
 toast.success(`${totalPieces} pieces ÷ ${perBag} per bag = ${unitsPerBatch} units per batch`);
 } else {
 // If pieces per bag not set, just show total pieces
 setFormData(prev => ({ ...prev, units_per_batch: totalPieces }));
 toast.info(`${totalPieces} total pieces (set "Pieces per Bag" to calculate units)`);
 }
 }
 };

 // Calculate pieces per tray from piece size
 // Auto-calculate pieces per tray when piece size or tray size changes
 useEffect(() => {
 if (selectedTraySize && pieceSize && parseFloat(pieceSize) > 0) {
 const tray = TRAY_SIZES[selectedTraySize];
 const traySqIn = tray.width * tray.depth;
 const pieceSqIn = parseFloat(pieceSize);
 const calculatedPieces = Math.floor(traySqIn / pieceSqIn);
 setPiecesPerTray(calculatedPieces);
 }
 }, [selectedTraySize, pieceSize]);

 const calculatePiecesFromSize = () => {
 if (!selectedTraySize || !pieceSize) {
 toast.error('Please select tray size and enter piece size');
 return;
 }
 
 const tray = TRAY_SIZES[selectedTraySize];
 const traySqIn = tray.width * tray.depth;
 const pieceSqIn = parseFloat(pieceSize) || 1;
 const calculatedPieces = Math.floor(traySqIn / pieceSqIn);
 
 setPiecesPerTray(calculatedPieces);
 toast.success(`${calculatedPieces} pieces fit on ${selectedTraySize} tray (${traySqIn.toFixed(1)} sq in ÷ ${pieceSqIn} sq in per piece)`);
 };

 // Calculate piece-based ingredient cost
 useEffect(() => {
 const pieces = parseFloat(pieceCostCalc.piecesInBag) || 0;
 const costPerBag = parseFloat(pieceCostCalc.costPerBag) || 0;
 const bagWeight = parseFloat(pieceCostCalc.bagWeight) || 0;
 
 if (pieces > 0 && costPerBag > 0) {
 const costPerPiece = costPerBag / pieces;
 setPieceCostCalc(prev => ({ ...prev, costPerPiece: costPerPiece.toFixed(4) }));
 }
 
 if (bagWeight > 0 && costPerBag > 0) {
 const costPerUnit = costPerBag / bagWeight;
 setPieceCostCalc(prev => ({ ...prev, costPerUnit: costPerUnit.toFixed(2) }));
 }
 }, [pieceCostCalc.piecesInBag, pieceCostCalc.costPerBag, pieceCostCalc.bagWeight]);

 // AI query for water content
 const askAIForWaterContent = async () => {
 if (!formData.product_description && !formData.plan_name) {
 toast.error('Please enter product name or description first');
 return;
 }
 
 setAiLoading(true);
 try {
 const productName = formData.product_description || formData.plan_name;
 const response = await axios.post(`${API}/calculator/ai-estimate`, {
 field_name: 'water_content',
 user_prompt: `What percentage of water weight is in ${productName}? Please provide only the percentage number (e.g., 85 for 85% water content).`,
 context: {
 country: user?.country || 'United States',
 currency: formData.currency,
 unit_system: formData.unit_system
 },
 form_values: {
 product_description: productName
 },
 use_web_search: true
 });
 
 if (response.data && response.data.suggested_value) {
 setWaterContentPercent(response.data.suggested_value);
 toast.success(`AI estimated water content: ${response.data.suggested_value}%`);
 }
 } catch (error) {
 toast.error('Failed to get AI estimate for water content');
 console.error(error);
 } finally {
 setAiLoading(false);
 }
 };

 // Calculate dry weight from wet weight and water content
 // Auto-calculate dry weight when wet weight or water content changes
 useEffect(() => {
 const wet = parseFloat(wetWeightPerTray) || 0;
 const waterPercent = parseFloat(waterContentPercent) || 0;
 
 if (wet && waterPercent) {
 const dryWeight = wet * (1 - waterPercent / 100);
 setDryWeightPerTray(dryWeight.toFixed(2));
 }
 }, [wetWeightPerTray, waterContentPercent]);

 const calculateDryWeight = () => {
 const wet = parseFloat(wetWeightPerTray) || 0;
 const waterPercent = parseFloat(waterContentPercent) || 0;
 
 if (wet && waterPercent) {
 const dryWeight = wet * (1 - waterPercent / 100);
 setDryWeightPerTray(dryWeight.toFixed(2));
 toast.success(`Dry weight: ${dryWeight.toFixed(2)} lbs (${wet} lbs - ${waterPercent}% water)`);
 }
 };

 // AI query for selling price
 const [aiPriceExplanation, setAiPriceExplanation] = useState('');
 
 const askAIForSellingPrice = async () => {
 if (!formData.product_description && !formData.plan_name) {
 toast.error('Please enter product name or description first');
 return;
 }
 
 setAiLoading(true);
 setAiPriceExplanation('');
 try {
 const productName = formData.product_description || formData.plan_name;
 const packageSize = quantityPerUnit ? 
 `${quantityPerUnit} ${unitType === 'ounces' ? 'oz' : unitType === 'pounds' ? 'lb' : unitType}` : 
 'per unit';
 
 const response = await axios.post(`${API}/calculator/ai-estimate`, {
 field_name: 'selling_price',
 user_prompt: `Search for current retail prices of freeze-dried ${productName} products (package size: ${packageSize}). Return: 1) A single estimated price in USD (provide a mid-range retail price, not wholesale), 2) Brief explanation of the price range you found and where/how you found it (mention if from Amazon, specialty retailers, etc.). Format: Just the number for price, then explanation.`,
 context: {
 country: user?.country || 'United States',
 currency: formData.currency,
 unit_system: formData.unit_system
 },
 form_values: {
 product_description: productName,
 package_size: packageSize
 },
 use_web_search: true
 });
 
 if (response.data && response.data.suggested_value) {
 setFormData(prev => ({ ...prev, selling_price: response.data.suggested_value }));
 setAiPriceExplanation(response.data.explanation || 'Price estimated from web search');
 toast.success(`AI estimated selling price: $${response.data.suggested_value}`, { duration: 5000 });
 }
 } catch (error) {
 toast.error('Failed to get AI price estimate');
 console.error(error);
 } finally {
 setAiLoading(false);
 }
 };

 // Calculate units per batch based on unit type
 const calculateUnitsPerBatch = () => {
 const trays = parseFloat(numTrays) || 0;
 const qtyPerUnit = parseFloat(quantityPerUnit) || 1;
 
 if (!trays) {
 toast.error('Please enter number of trays');
 return;
 }

 let totalQuantity = 0;
 
 if (unitType === 'pieces') {
 totalQuantity = (parseFloat(piecesPerTray) || 0) * trays;
 } else {
 // For weight-based units
 const weightPerTray = parseFloat(dryWeightPerTray) || parseFloat(wetWeightPerTray) || 0;
 totalQuantity = weightPerTray * trays;
 
 // Convert to requested unit
 if (unitType === 'grams') {
 totalQuantity = totalQuantity * 453.592; // lbs to grams
 } else if (unitType === 'ounces') {
 totalQuantity = totalQuantity * 16; // lbs to ounces
 }
 }
 
 const unitsPerBatch = Math.floor(totalQuantity / qtyPerUnit);
 setFormData(prev => ({ ...prev, units_per_batch: unitsPerBatch }));
 
 const unitLabel = unitType === 'pieces' ? 'pieces' : unitType;
 toast.success(`${totalQuantity.toFixed(2)} total ${unitLabel} ÷ ${qtyPerUnit} per unit = ${unitsPerBatch} units per batch`);
 };

 // Validate batches per week against cycle time
 const validateBatchesPerWeek = (batchesPerWeek) => {
 const cycleTime = parseFloat(formData.cycle_time_hours) || 24;
 const maxBatchesPerWeek = (7 * 24) / cycleTime;
 const utilization = (batchesPerWeek / maxBatchesPerWeek) * 100;
 
 if (batchesPerWeek > maxBatchesPerWeek) {
 toast.error(`Maximum possible batches/week is ${Math.floor(maxBatchesPerWeek)} with ${cycleTime}hr cycles. You entered ${batchesPerWeek}.`);
 return false;
 }
 
 if (utilization > 80) {
 toast.warning(`High utilization: ${utilization.toFixed(0)}% - Consider maintenance time`);
 }
 
 return true;
 };

 // Helper function to convert weight units
 const convertWeight = (weight, fromUnit, toUnit) => {
 if (fromUnit === toUnit) return weight;
 
 // Convert to pounds first (base unit)
 let weightInLbs = weight;
 if (fromUnit === 'oz') weightInLbs = weight / 16;
 else if (fromUnit === 'kg') weightInLbs = weight * 2.20462;
 else if (fromUnit === 'g') weightInLbs = weight / 453.592;
 
 // Convert from pounds to target unit
 if (toUnit === 'lbs') return weightInLbs;
 if (toUnit === 'oz') return weightInLbs * 16;
 if (toUnit === 'kg') return weightInLbs / 2.20462;
 if (toUnit === 'g') return weightInLbs * 453.592;
 
 return weightInLbs;
 };

 // Auto-calculate ingredient cost from per-unit and weight
 const handleIngredientCostPerLb = (value) => {
 const costPerUnit = parseFloat(value) || 0;
 
 // Calculate total weight from batch basics
 let totalWeight = 0;
 if (calculationMethod === 'weight') {
 totalWeight = (parseFloat(wetWeightPerTray) || 0) * (parseFloat(numTrays) || 0);
 } else if (calculationMethod === 'pieces') {
 totalWeight = parseFloat(totalBatchWeight) || 0;
 }
 
 // For weight calculation, convert units if they don't match
 let totalCost = 0;
 if (calculationMethod === 'weight' && totalWeight > 0) {
 const costUnit = (formData.ingredient_cost_unit || '$/lb').replace('$/', '');
 const weightUnit = formData.ingredient_weight_unit || 'lbs';
 
 // Convert weight to match cost unit
 const convertedWeight = convertWeight(totalWeight, weightUnit, costUnit);
 totalCost = costPerUnit * convertedWeight;
 } else if (calculationMethod === 'pieces' && totalWeight > 0) {
 // For pieces, direct multiplication
 totalCost = costPerUnit * totalWeight;
 }
 
 setFormData(prev => ({
 ...prev,
 ingredient_cost_per_lb: value,
 total_ingredient_weight_lb: totalWeight.toString(),
 ingredient_cost: totalCost.toFixed(2)
 }));
 };

 const handleIngredientWeight = (value) => {
 // This function is no longer used since weight is auto-calculated
 // Kept for backwards compatibility
 const weight = parseFloat(value) || 0;
 setFormData(prev => {
 const perLb = parseFloat(prev.ingredient_cost_per_lb) || 0;
 return {
 ...prev,
 total_ingredient_weight_lb: value,
 ingredient_cost: perLb > 0 ? (perLb * weight).toFixed(2) : prev.ingredient_cost
 };
 });
 };

 // Auto-calculate packaging cost from per-unit
 const handlePackagingCostPerUnit = (value) => {
 const perUnit = parseFloat(value) || 0;
 setFormData(prev => {
 const units = parseFloat(prev.units_per_batch) || 0;
 return {
 ...prev,
 packaging_cost_per_unit: value,
 packaging_cost: units > 0 ? (perUnit * units).toFixed(2) : prev.packaging_cost
 };
 });
 };

 // Auto-calculate electricity cost from kWh rate, power draw, and cycle time
 const handleElectricityCostPerHour = (value, field = 'electricity_rate_kwh') => {
 setFormData(prev => {
 // Update the specific field that changed
 const updatedFormData = { ...prev, [field]: value };
 
 const kwhRate = parseFloat(updatedFormData.electricity_rate_kwh) || 0;
 const powerKw = parseFloat(updatedFormData.machine_power_kw) || 0;
 const hours = parseFloat(updatedFormData.cycle_time_hours) || 0;
 
 // Calculate total cost: kWh rate × kW × hours
 const totalCost = kwhRate * powerKw * hours;
 const costPerHour = kwhRate * powerKw;
 
 return {
 ...updatedFormData,
 electricity_cost_per_hour: costPerHour.toFixed(3),
 electricity_cost: totalCost.toFixed(2)
 };
 });
 };

 const handleCycleTimeHours = (value) => {
 const hours = parseFloat(value) || 0;
 setFormData(prev => {
 const perHour = parseFloat(prev.electricity_cost_per_hour) || 0;
 return {
 ...prev,
 cycle_time_hours: value,
 electricity_cost: perHour > 0 ? (perHour * hours).toFixed(2) : prev.electricity_cost
 };
 });
 };

 // New handlers for enhanced Direct Costs
 const handlePackagingItemChange = (field, value) => {
 setFormData(prev => {
 const newFormData = { ...prev, [field]: value };
 
 // Calculate total packaging cost per unit
 const bag = parseFloat(newFormData.packaging_bag_cost) || 0;
 const label = parseFloat(newFormData.packaging_label_cost) || 0;
 const o2 = parseFloat(newFormData.packaging_o2_cost) || 0;
 const seal = parseFloat(newFormData.packaging_seal_cost) || 0;
 const other = parseFloat(newFormData.packaging_other_cost) || 0;
 const perUnit = bag + label + o2 + seal + other;
 
 // Calculate total for batch
 const units = parseFloat(newFormData.units_per_batch) || 0;
 const total = perUnit * units;
 
 return {
 ...newFormData,
 packaging_cost_per_unit: perUnit.toFixed(2),
 packaging_cost: total.toFixed(2)
 };
 });
 };

 const handleLaborPhaseChange = (field, value) => {
 setFormData(prev => {
 const newFormData = { ...prev, [field]: value };
 
 // Calculate total labor hours
 const prepHours = parseFloat(newFormData.labor_prep_hours) || 0;
 const finishHours = parseFloat(newFormData.labor_finish_hours) || 0;
 const packHours = parseFloat(newFormData.labor_pack_hours) || 0;
 const totalHours = prepHours + finishHours + packHours;
 
 return {
 ...newFormData,
 labor_hours: totalHours.toFixed(2)
 };
 });
 };

 const handleConsumableItemChange = (field, value) => {
 setFormData(prev => {
 const newFormData = { ...prev, [field]: value };
 
 // Calculate total consumables cost
 const pumpOil = parseFloat(newFormData.consumable_pump_oil) || 0;
 const trayLiners = parseFloat(newFormData.consumable_tray_liners) || 0;
 const cleaning = parseFloat(newFormData.consumable_cleaning) || 0;
 const other = parseFloat(newFormData.consumable_other) || 0;
 const total = pumpOil + trayLiners + cleaning + other;
 
 return {
 ...newFormData,
 consumables_cost: total.toFixed(2)
 };
 });
 };

 const askAIForPowerRate = async () => {
 if (!formData.location && !user?.city) {
 toast.error('Please enter your location first');
 return;
 }
 
 setAiLoading(true);
 try {
 const location = formData.location || `${user?.city}, ${user?.state}, ${user?.country}`;
 const response = await axios.post(`${API}/calculator/ai-estimate`, {
 field_name: 'electricity_rate_kwh',
 user_prompt: `What is the average residential electricity rate ($/kWh) in ${location}? Please provide ONLY the $/kWh rate as a number (e.g., 0.12 for 12 cents per kWh). Do not convert to hourly cost.`,
 context: {
 location: location,
 country: user?.country || 'United States',
 currency: formData.currency,
 equipment: 'freeze dryer'
 },
 form_values: formData,
 use_web_search: true
 });
 
 if (response.data && response.data.suggested_value) {
 const kwhRate = parseFloat(response.data.suggested_value);
 const powerKw = parseFloat(formData.machine_power_kw) || 1.5;
 const hours = parseFloat(formData.cycle_time_hours) || 24;
 
 const costPerHour = kwhRate * powerKw;
 const totalCost = kwhRate * powerKw * hours;
 
 setFormData(prev => ({ 
 ...prev, 
 electricity_rate_kwh: kwhRate.toString(),
 electricity_cost_per_hour: costPerHour.toFixed(2),
 electricity_cost: totalCost.toFixed(2)
 }));
 setAiEstimatedFields({
 ...aiEstimatedFields,
 electricity_rate_kwh: {
 value: kwhRate.toString(),
 explanation: response.data.explanation
 }
 });
 toast.success('AI estimated local power rate');
 }
 } catch (error) {
 toast.error('Failed to get power rate estimate');
 console.error(error);
 } finally {
 setAiLoading(false);
 }
 };

 const askAIForCycleTime = async () => {
 if (!formData.product_description && !formData.plan_name) {
 toast.error('Please enter a product name or description first');
 return;
 }
 
 setAiLoading(true);
 try {
 const product = formData.product_description || formData.plan_name;
 const response = await axios.post(`${API}/calculator/ai-estimate`, {
 field_name: 'cycle_time_hours',
 user_prompt: `What is the typical freeze-drying cycle time in hours for ${product}? Please search online for typical freeze-dry times for this product and provide ONLY the total number of hours including freeze time, primary drying, secondary drying, and defrost time. Provide just the number (e.g., 24 for 24 hours). Consider this is for a home/small commercial freeze dryer.`,
 context: {
 product: product,
 freeze_dryer: formData.freeze_dryer_model || 'home freeze dryer',
 unit: 'hours'
 },
 form_values: formData,
 use_web_search: true
 });
 
 if (response.data && response.data.suggested_value) {
 const cycleHours = parseFloat(response.data.suggested_value);
 
 setCycleTimeHours(cycleHours.toString());
 setFormData(prev => ({ 
 ...prev, 
 cycle_time_hours: cycleHours.toString()
 }));
 setAiCycleTimeEstimated(true);
 
 // Also update electricity cost if rate is already set
 if (formData.electricity_rate_kwh && formData.machine_power_kw) {
 const kwhRate = parseFloat(formData.electricity_rate_kwh);
 const powerKw = parseFloat(formData.machine_power_kw);
 const totalCost = kwhRate * powerKw * cycleHours;
 setFormData(prev => ({ 
 ...prev, 
 electricity_cost: totalCost.toFixed(2)
 }));
 }
 
 toast.success(`AI estimated cycle time for ${product}`);
 }
 } catch (error) {
 toast.error('Failed to get cycle time estimate');
 console.error(error);
 } finally {
 setAiLoading(false);
 }
 };

 const askAIForMachinePower = async () => {
 if (!formData.freeze_dryer_model) {
 toast.error('Please select your freeze dryer model in Batch Basics first');
 return;
 }
 
 let freezeDryerModel = formData.freeze_dryer_model;
 
 // If "Other / Custom", prompt for manual entry
 if (freezeDryerModel === 'Other / Custom') {
 const customModel = prompt('What freeze dryer do you have? (e.g., "Freeze Dry Pro", "Custom Build")');
 if (!customModel || !customModel.trim()) {
 return;
 }
 freezeDryerModel = customModel;
 }
 
 setAiLoading(true);
 try {
 const response = await axios.post(`${API}/calculator/ai-estimate`, {
 field_name: 'machine_power_kw',
 user_prompt: `What is the power consumption in kilowatts (kW) for a ${freezeDryerModel} freeze dryer? Please search online for the official specifications and provide ONLY the kW number (e.g., 1.5 for 1500 watts or 1.5 kW). If you find wattage, convert it to kW by dividing by 1000. Be precise and search for the exact model specifications.`,
 context: {
 equipment: freezeDryerModel,
 unit: 'kW',
 models_list: 'Small Home, Medium Home, Large Home, Extra Large Home, Commercial HRC-class, Mid-size Cabinet, Large Cabinet, Industrial Cabinet, Custom / Other'
 },
 form_values: formData,
 use_web_search: true
 });
 
 if (response.data && response.data.suggested_value) {
 const powerKw = parseFloat(response.data.suggested_value);
 const kwhRate = parseFloat(formData.electricity_rate_kwh) || 0;
 const hours = parseFloat(formData.cycle_time_hours) || 24;
 
 const costPerHour = kwhRate * powerKw;
 const totalCost = kwhRate * powerKw * hours;
 
 setFormData(prev => ({ 
 ...prev, 
 machine_power_kw: powerKw.toString(),
 electricity_cost_per_hour: costPerHour.toFixed(2),
 electricity_cost: totalCost.toFixed(2)
 }));
 setAiEstimatedFields({
 ...aiEstimatedFields,
 machine_power_kw: {
 value: powerKw.toString(),
 explanation: response.data.explanation
 }
 });
 toast.success(`Found power specs for ${freezeDryerModel}`);
 }
 } catch (error) {
 toast.error('Failed to get machine power specs');
 console.error(error);
 } finally {
 setAiLoading(false);
 }
 };

 // Calculate max units from shelf space
 const calculateUnitsFromShelfSpace = () => {
 const shelfSqFt = parseFloat(formData.shelf_space_sq_ft) || 0;
 const unitFootprintSqIn = parseFloat(formData.unit_footprint_sq_in) || 0;
 
 if (shelfSqFt === 0 || unitFootprintSqIn === 0) {
 toast.error('Please enter shelf space and unit footprint');
 return;
 }

 const shelfSqIn = shelfSqFt * 144; // Convert sq ft to sq inches
 const maxUnits = Math.floor(shelfSqIn / unitFootprintSqIn);
 
 setFormData({ ...formData, units_per_batch: maxUnits });
 toast.success(`Maximum ${maxUnits} units fit on ${shelfSqFt} sq ft of shelf space`);
 };

 // Field descriptions for business planning context
 const fieldDescriptions = {
 ingredient_cost_per_lb: "Cost per pound/kg of raw ingredients. Enables precise scaling and seasonal price tracking. Essential for calculating true COGS per unit.",
 total_ingredient_weight_lb: "Total weight of ingredients per batch. Multiply by cost/lb to get total ingredient cost. Critical for inventory planning and scaling.",
 ingredient_cost: "Total raw materials cost per batch. Auto-calculates from weight × cost/lb. Critical for COGS and profit margins.",
 packaging_cost_per_unit: "Cost per individual package (bag, label, O2 absorber). Multiply by units/batch for total. Track bulk discounts as you scale.",
 packaging_cost: "Total packaging cost per batch. Auto-calculates from per-unit cost × units/batch. Often underestimated in initial planning.",
 labor_hours: "Prep, loading, unloading, packaging time per batch. Key driver of scaling costs - automation opportunities here save most money.",
 labor_rate: "Hourly wage including payroll taxes. For lenders: shows realistic overhead. Plan for raises as you scale.",
 electricity_cost_per_hour: "Energy cost per hour of operation. Check your electric bill for kWh rate, then multiply by machine watts. Varies by region (CA: $0.20-0.30/kWh, TX: $0.10-0.15/kWh).",
 cycle_time_hours: "Total freeze-dry cycle time (typically 24-30 hours). Multiply by hourly electricity cost. Shorter cycles = higher throughput.",
 electricity_cost: "Total energy cost per cycle. Auto-calculates from hourly rate × cycle time. Significant cost in high-volume ops—consider time-of-use rates.",
 consumables_cost: "Pump oil, tray liners, O2 absorbers, cleaning supplies per batch. Recurring cost often forgotten - budget 3-5% of revenue.",
 waste_loss: "Spoilage, breakage, quality rejects per batch. For lenders: shows realistic yield expectations. Track actual vs. planned to improve efficiency.",
 shelf_space_sq_ft: "Total usable freeze dryer tray space (sq ft). Determines maximum units per batch. Example: a Large home unit is ~2.8 sq ft.",
 unit_footprint_sq_in: "Space each packaged unit occupies on tray (sq inches). Include spacing between units. Example: 4oz bag ≈ 12-16 sq in.",
 monthly_rent: "Facility cost or home-based space allocation. For commercial: include utilities base. Shows lenders you've secured adequate workspace.",
 monthly_insurance: "Business liability, product insurance. Required for wholesale/retail. Shows professional operation to lenders and buyers.",
 monthly_software: "Inventory, accounting, e-commerce platforms. Essential for scaling. Lenders want to see proper financial tracking systems.",
 monthly_marketing: "Ads, website, social media, local marketing. Plan 10-15% of revenue for growth phase. Shows lenders your customer acquisition strategy.",
 monthly_maintenance: "Equipment servicing, repairs, replacement parts. Critical for uptime. Lenders assess business continuity risk here.",
 machine_price: "Initial equipment investment. For loans: primary collateral. Depreciation impacts taxes and breakeven calculations.",
 units_per_batch: "Production output per cycle. Key efficiency metric. Can calculate from shelf space ÷ unit footprint. Lenders evaluate capacity utilization.",
 batches_per_month: "Monthly production frequency. Impacts equipment ROI and breakeven timeline. Shows operational rhythm and scaling potential.",
 sales_tax_rate: "State/local sales tax percentage. Essential for accurate net revenue projections and cash flow planning.",
 income_tax_rate: "Federal + state business tax rate. Critical for after-tax profit analysis. Shows lenders you understand true bottom line."
 };

 // FieldInfo and AIButton are now imported components

 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800">
 <ToolSelector />
 <div className="container mx-auto px-4 pt-4">
   <AdDisplay location="business_header" className="flex justify-center mb-4" />
 </div>
 {/* Dashboard-style navy hero header */}
 <div className="bg-navy-spotlight text-background relative overflow-hidden" data-testid="business-calc-hero">
 <div className="container mx-auto px-4 py-8 sm:py-10 relative z-10">
 <div className="flex items-center gap-3 mb-4">
 <Button
 variant="ghost"
 onClick={() => navigate('/dashboard')}
 className="text-background/80 hover:text-background hover:bg-background/10 rounded-full h-9 px-3"
 data-testid="back-to-dashboard"
 >
 <ArrowLeft className="w-4 h-4 mr-1.5" />
 Dashboard
 </Button>
 <div className="ml-auto">
 <ThemeToggle />
 </div>
 </div>
 <div className="max-w-3xl">
 <span className="pill-badge"><Calculator className="w-3.5 h-3.5" /> Business planning</span>
 <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4 text-balance">
 Business <span className="text-amber-brand">Calculator</span>
 </h1>
 <p className="mt-3 text-base sm:text-lg text-background/70 max-w-xl">
 Model costs, margins and break-even for a real freeze-dried business — inputs on the left, live numbers on the right.
 </p>
 </div>
 </div>
 {/* Soft amber glows */}
 <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl pointer-events-none" />
 <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
 </div>

 <div className={`container mx-auto px-4 py-8 max-w-7xl view-${viewMode}`}>
 {/* Professional Print Styles */}
 <style>{`
 @media print {
 /* Clean white background */
 body { 
 background: white !important;
 margin: 0;
 padding: 20px;
 }
 
 /* Hide interactive elements */
 .no-print { display: none !important; }
 button:not(.print-show) { display: none !important; }
 
 /* Show print-only elements */
 .print-show { display: block !important; }
 
 /* Page setup */
 @page {
 margin: 0.5in;
 size: letter;
 }
 
 /* Clean card borders for print */
 .border, .border-2 {
 border: 1px solid #cbd5e0 !important;
 }
 
 /* Improve table readability */
 table {
 page-break-inside: avoid;
 border-collapse: collapse;
 }
 
 th, td {
 border: 1px solid #e2e8f0;
 }
 
 /* Color adjustments for print */
 .bg-gradient-to-br,
 .bg-gradient-to-r,
 .bg-blue-50,
 .bg-orange-50,
 .bg-primary/5,
 .bg-green-50,
 .bg-red-50,
 .bg-amber-50,
 .bg-cyan-50 {
 background: #f8fafc !important;
 print-color-adjust: exact;
 -webkit-print-color-adjust: exact;
 }
 
 /* Keep important color highlights */
 .text-green-700,
 .text-green-600 {
 color: #15803d !important;
 }
 
 .text-red-700,
 .text-red-600 {
 color: #b91c1c !important;
 }
 
 .text-blue-700,
 .text-primary {
 color: #1d4ed8 !important;
 }
 
 /* Improve spacing */
 .space-y-4 > * + * {
 margin-top: 1rem;
 }
 
 /* Hide empty results section */
 .lg\\:col-span-2 { display: none !important; }
 
 /* Make results full width */
 .space-y-4 {
 width: 100% !important;
 max-width: none !important;
 }
 }
 
 /* Screen-only: hide print footer */
 .print-show {
 display: none;
 }
 
 @media print {
 .print-show {
 display: block !important;
 }
 }
 
 /* Mobile View Mode */
 .view-mobile {
 max-width: 480px !important;
 margin: 0 auto !important;
 }
 
 .view-mobile * {
 font-size: 14px !important;
 }
 
 .view-mobile table {
 font-size: 12px !important;
 }
 
 .view-mobile .text-2xl {
 font-size: 1.25rem !important;
 }
 
 .view-mobile .text-xl {
 font-size: 1.125rem !important;
 }
 
 .view-mobile .px-4 {
 padding-left: 0.5rem !important;
 padding-right: 0.5rem !important;
 }
 
 .view-mobile .py-8 {
 padding-top: 1rem !important;
 padding-bottom: 1rem !important;
 }
 
 /* Desktop View Mode (default) */
 .view-desktop {
 max-width: 100% !important;
 }
 
 .view-desktop .container {
 max-width: 100% !important;
 }
 `}</style>
 
 {/* Profile Completion Check - Redirect happens in useEffect */}
 
 {/* Action Buttons */}
 <div className="flex flex-wrap gap-2 mb-6 no-print">
 <Button 
 variant="outline" 
 onClick={createNewPlan}
 className="dark:text-white dark:border-slate-600"
 >
 <FileText className="w-4 h-4 mr-2" />
 New Plan
 </Button>

 <Button
 onClick={loadExample}
 className="rounded-md font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
 data-testid="load-example-btn"
 >
 <Wand2 className="w-4 h-4 mr-2" />
 Try me — load example plan
 </Button>
 
 <Dialog open={showLoadPlanDialog} onOpenChange={setShowLoadPlanDialog}>
 <DialogTrigger asChild>
 <Button variant="outline" className="dark:text-white dark:border-slate-600">
 <FileText className="w-4 h-4 mr-2" />
 Load Plan
 </Button>
 </DialogTrigger>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Load Saved Plan</DialogTitle>
 <DialogDescription>Choose a plan to load</DialogDescription>
 </DialogHeader>
 <div className="space-y-2 max-h-96 overflow-y-auto">
 {savedPlans.length === 0 ? (
 <p className="text-center text-muted-foreground py-4">No saved plans</p>
 ) : (
 savedPlans.map((plan) => (
 <Card key={plan.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
 <CardContent className="p-4 flex items-center justify-between gap-3" onClick={() => loadPlan(plan)}>
 <div className="flex-1">
 <h3 className="font-semibold">{plan.plan_name}</h3>
 <p className="text-sm text-muted-foreground">
 {new Date(plan.updated_at).toLocaleDateString()}
 </p>
 </div>
 <Button
 variant="ghost"
 size="sm"
 onClick={(e) => deletePlan(plan.id, plan.plan_name, e)}
 className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
 >
 🗑️ Delete
 </Button>
 </CardContent>
 </Card>
 ))
 )}
 </div>
 </DialogContent>
 </Dialog>

 <Dialog open={showSavePlanDialog} onOpenChange={setShowSavePlanDialog}>
 <DialogTrigger asChild>
 <Button variant="outline" className="dark:text-white dark:border-slate-600">
 <Save className="w-4 h-4 mr-2" />
 Save Plan
 </Button>
 </DialogTrigger>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Save Calculator Plan</DialogTitle>
 <DialogDescription>Give your plan a name</DialogDescription>
 </DialogHeader>
 <div className="space-y-4 py-4">
 <Input
 value={formData.plan_name}
 onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
 placeholder="My Business Plan"
 />
 <Button onClick={savePlan} className="w-full">
 Save
 </Button>
 </div>
 </DialogContent>
 </Dialog>

 <Button variant="outline" onClick={exportPDF} className="dark:text-white dark:border-slate-600">
 <Download className="w-4 h-4 mr-2" />
 Export PDF
 </Button>
 </div>

 {/* Tab Navigation — pill toggle */}
 <div className="mb-6 no-print" data-testid="business-tabs">
 <div className="inline-flex items-center rounded-full bg-muted p-1 border border-border">
 <button
 type="button"
 onClick={() => setActiveTab('inputs')}
 data-testid="tab-inputs"
 className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-heading font-extrabold transition-all ${
 activeTab === 'inputs'
 ? 'bg-primary text-primary-foreground shadow'
 : 'text-foreground/70 hover:text-foreground'
 }`}
 >
 <FileText className="w-4 h-4" /> Inputs
 </button>
 <button
 type="button"
 onClick={() => outputs && setActiveTab('results')}
 disabled={!outputs}
 data-testid="tab-results"
 className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-heading font-extrabold transition-all ${
 activeTab === 'results'
 ? 'bg-primary text-primary-foreground shadow'
 : outputs
 ? 'text-foreground/70 hover:text-foreground'
 : 'text-muted-foreground/60 cursor-not-allowed'
 }`}
 >
 <TrendingUp className="w-4 h-4" /> Results
 {!outputs && <span className="text-[10px] font-normal opacity-70">(calculate first)</span>}
 </button>
 </div>
 </div>

 {/* Step-progress rail — visible while filling inputs */}
 {activeTab === 'inputs' && (
 <CalculatorStepRail
 steps={[
 { id: 'section-plan', label: 'Product', helper: 'Name & context' },
 { id: 'section-batch', label: 'Batch', helper: 'Machine & trays' },
 { id: 'section-costs', label: 'Direct costs', helper: 'Ingredients, packaging, labor' },
 { id: 'section-opex', label: 'OpEx', helper: 'Monthly overhead' },
 { id: 'section-capex', label: 'CapEx', helper: 'Machine & startup' },
 ]}
 currentIndex={businessCalcStep}
 onStepClick={(i) => setBusinessCalcStep(i)}
 completed={{
 0: !!formData.plan_name && !!formData.product_description,
 1: !!formData.freeze_dryer_model && (!!formData.units_per_batch || !!formData.cycle_time_hours),
 2: !!formData.ingredient_cost || !!formData.packaging_cost,
 3: !!formData.batches_per_month,
 4: !!formData.machine_price,
 }}
 />
 )}

 {/* Main Disclaimer — condensed HomeLyph Pro tone (was a loud red block) */}
 <Card className="border border-primary/40 bg-primary/5 mb-6 no-print">
 <CardContent className="pt-4">
 <div className="flex gap-3">
 <div className="flex-shrink-0">
 <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
 <ShieldCheck className="w-4 h-4" />
 </div>
 </div>
 <div className="flex-1">
 <h3 className="font-heading font-extrabold text-foreground text-sm mb-1">
 Educational tool — hypothetical numbers only
 </h3>
 <p className="text-sm text-foreground/80 leading-relaxed">
 Every figure below is derived from what <em>you</em> type in.
 Freeze Dry Academy and its affiliates make no warranties and take no
 liability for business decisions made from this output. Please verify with a
 CPA / advisor before acting.
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Onboarding tutorial */}
 <CalculatorTutorial
 storageKey="business-calc-v1"
 title="How the Business Calculator works"
 steps={[
 {
 title: '1 · Describe the product',
 body: 'Enter the plan / product name and a short description. This context helps the AI-driven market estimates below make sense — the more specific, the better.',
 },
 {
 title: '2 · Pick the machine + batch size',
 body: 'Choose your freeze dryer model and either pieces-per-tray or dry-weight per tray. The calculator uses this to work out units-per-batch automatically.',
 },
 {
 title: '3 · Log the costs',
 body: 'Ingredient, packaging, energy and any fixed overhead. Anything you leave blank is treated as zero — you can iterate as you learn your real numbers.',
 },
 {
 title: '4 · Read the results',
 body: 'Switch to the "Financial Results" tab. You get margin, break-even units, monthly production capacity and a plain-language plan you can share with a partner or lender.',
 },
 {
 title: '5 · Save & share',
 body: 'Use "Save Plan" to store this in your portal, "Export PDF" to send it to an accountant, or "New Plan" to start fresh. All numbers stay on your account.',
 },
 ]}
 />

 {/* Inputs Tab */}
 {activeTab === 'inputs' && (
 <div className="grid gap-6 lg:grid-cols-3" data-testid="business-inputs-layout">
 <div className="lg:col-span-2 space-y-4">
 {/* Header Ad */}
 <AdPlacement placement="header" />
 
 {/* Plan Info */}
 <div id="section-plan" className="print-plan-info scroll-mt-40">
 <PlanInformation
 showPlanInfo={showPlanInfo}
 setShowPlanInfo={setShowPlanInfo}
 formData={formData}
 setFormData={setFormData}
 user={user}
 aiEstimatedFields={aiEstimatedFields}
 openAIDialog={openAIDialog}
 />
 </div>

 {/* Batch Basics */}
 <div id="section-batch" className="print-batch-basics scroll-mt-40">
 <BatchBasics
 showBatchBasics={showBatchBasics}
 setShowBatchBasics={setShowBatchBasics}
 selectedTraySize={selectedTraySize}
 handleTraySelection={handleTraySelection}
 numTrays={numTrays}
 handleNumTrays={handleNumTrays}
 formData={formData}
 setFormData={setFormData}
 cycleTimeHours={cycleTimeHours}
 setCycleTimeHours={setCycleTimeHours}
 askAIForCycleTime={askAIForCycleTime}
 aiCycleTimeEstimated={aiCycleTimeEstimated}
 calculationMethod={calculationMethod}
 setCalculationMethod={setCalculationMethod}
 pieceSize={pieceSize}
 setPieceSize={setPieceSize}
 piecesPerTray={piecesPerTray}
 setPiecesPerTray={setPiecesPerTray}
 piecesPerBag={piecesPerBag}
 setPiecesPerBag={setPiecesPerBag}
 totalBatchWeight={totalBatchWeight}
 setTotalBatchWeight={setTotalBatchWeight}
 waterContentPercent={waterContentPercent}
 setWaterContentPercent={setWaterContentPercent}
 askAIForWaterContent={askAIForWaterContent}
 aiLoading={aiLoading}
 wetWeightPerTray={wetWeightPerTray}
 setWetWeightPerTray={setWetWeightPerTray}
 dryWeightPerTray={dryWeightPerTray}
 setDryWeightPerTray={setDryWeightPerTray}
 quantityPerUnit={quantityPerUnit}
 setQuantityPerUnit={setQuantityPerUnit}
 unitType={unitType}
 setUnitType={setUnitType}
 askAIForSellingPrice={askAIForSellingPrice}
 aiPriceExplanation={aiPriceExplanation}
 />
 </div>

 {/* Recipe Integration */}
 <RecipeIntegration
 checkingAccess={checkingAccess}
 hasRecipeAccess={hasRecipeAccess}
 selectedRecipeId={selectedRecipeId}
 handleRecipeSelect={handleRecipeSelect}
 recipes={recipes}
 />

 {/* Direct Costs (COGS) */}
 <div id="section-costs" className="print-direct-costs scroll-mt-40">
 <DirectCosts
 showCOGS={showCOGS}
 setShowCOGS={setShowCOGS}
 formData={formData}
 setFormData={setFormData}
 numTrays={numTrays}
 calculationMethod={calculationMethod}
 wetWeightPerTray={wetWeightPerTray}
 dryWeightPerTray={dryWeightPerTray}
 piecesPerTray={piecesPerTray}
 aiEstimatedFields={aiEstimatedFields}
 openAIDialog={openAIDialog}
 openPieceCostCalculator={() => setShowPieceCostCalculator(true)}
 handleIngredientCostPerLb={handleIngredientCostPerLb}
 handleIngredientWeight={handleIngredientWeight}
 handlePackagingItemChange={handlePackagingItemChange}
 handleElectricityCostPerHour={handleElectricityCostPerHour}
 handleConsumableItemChange={handleConsumableItemChange}
 handleLaborPhaseChange={handleLaborPhaseChange}
 askAIForPowerRate={askAIForPowerRate}
 askAIForMachinePower={askAIForMachinePower}
 aiLoading={aiLoading}
 />
 </div>

 {/* Tax Rates */}
 <div className="print-tax-rates">
 <TaxRates
 showTaxRates={showTaxRates}
 setShowTaxRates={setShowTaxRates}
 formData={formData}
 setFormData={setFormData}
 aiEstimatedFields={aiEstimatedFields}
 openAIDialog={openAIDialog}
 />
 </div>

 {/* Operational Site Costs (OpEx) */}
 <div id="section-opex" className="print-opex scroll-mt-40">
 <OperationalCosts
 showOpEx={showOverhead}
 setShowOpEx={setShowOverhead}
 formData={formData}
 setFormData={setFormData}
 aiEstimatedFields={aiEstimatedFields}
 openAIDialog={openAIDialog}
 />
 </div>

 {/* Capital Cost Planner (CapEx) */}
 <div id="section-capex" className="print-capex scroll-mt-40">
 <CapitalCosts
 showCapEx={showEquipment}
 setShowCapEx={setShowEquipment}
 formData={formData}
 setFormData={setFormData}
 aiEstimatedFields={aiEstimatedFields}
 openAIDialog={openAIDialog}
 />
 </div>

 {/* Footer Ad */}
 <AdPlacement placement="footer" />

 <div className="space-y-3 no-print">
 <Button onClick={calculateResults} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg" size="lg" data-testid="calculate-btn">
 <Calculator className="w-5 h-5 mr-2" />
 Calculate Results & View Analysis →
 </Button>
 <p className="text-xs text-center text-muted-foreground">
 Click to generate complete financial analysis
 </p>
 </div>
 </div>

 {/* Live insights sidebar */}
 <div className="lg:col-span-1 order-first lg:order-last">
 <div className="lg:sticky lg:top-40">
 <LiveInsightsPanel
 formData={formData}
 outputs={outputs}
 currency={formData.currency}
 />
 </div>
 </div>
 </div>
 )}

      {/* Results Tab — extracted to BusinessResultsTab */}
      {activeTab === 'results' && (
        <BusinessResultsTab
          formData={formData}
          outputs={outputs}
          sensitivity={sensitivity}
          competitionData={competitionData}
          competitionLoading={competitionLoading}
          optimizationResults={optimizationResults}
          productNotes={productNotes}
          quantityPerUnit={quantityPerUnit}
          unitType={unitType}
          calculationMethod={calculationMethod}
          piecesPerBag={piecesPerBag}
          totalBatchWeight={totalBatchWeight}
          piecesPerTray={piecesPerTray}
          numTrays={numTrays}
          showProductionPlanning={showProductionPlanning}
          showMarketCompetition={showMarketCompetition}
          exportPDF={exportPDF}
          calculateSensitivity={calculateSensitivity}
          applyOptimization={applyOptimization}
          setOptimizationResults={setOptimizationResults}
          setShowProductionPlanning={setShowProductionPlanning}
          setShowMarketCompetition={setShowMarketCompetition}
          setShowSavePlanDialog={setShowSavePlanDialog}
          setProductNotes={setProductNotes}
          setAiBusinessSummary={setAiBusinessSummary}
          analyzeMarketCompetition={analyzeMarketCompetition}
          API={API}
          navigate={navigate}
          isDemoPlan={isDemoPlan}
          aiBusinessSummary={aiBusinessSummary}
        />
      )}

 {/* Units Calculator Dialog */}
 <Dialog open={showUnitsCalculator} onOpenChange={setShowUnitsCalculator}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Units Calculator</DialogTitle>
 <DialogDescription>
 Calculate how many units you&apos;ll get after freeze-drying based on raw weight
 </DialogDescription>
 </DialogHeader>
 <div className="space-y-4 py-4">
 <div className="space-y-2">
 <Label>Raw Product Weight ({formData.unit_system === 'metric' ? 'kg' : 'lbs'})</Label>
 <Input
 type="number"
 step="0.1"
 value={weightCalculator.raw_weight}
 onChange={(e) => setWeightCalculator({ ...weightCalculator, raw_weight: e.target.value })}
 placeholder={formData.unit_system === 'metric' ? '5' : '10'}
 />
 </div>
 <div className="space-y-2">
 <Label>Product Type</Label>
 <Select 
 value={weightCalculator.product_type} 
 onValueChange={(value) => setWeightCalculator({ 
 ...weightCalculator, 
 product_type: value,
 water_content: value === 'fruit' ? 80 : value === 'vegetable' ? 85 : value === 'meat' ? 70 : 80
 })}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="fruit">Fruit (80% water)</SelectItem>
 <SelectItem value="vegetable">Vegetable (85% water)</SelectItem>
 <SelectItem value="meat">Meat (70% water)</SelectItem>
 <SelectItem value="candy">Candy (10% water)</SelectItem>
 <SelectItem value="custom">Custom</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div className="space-y-2">
 <Label>Water Content (%)</Label>
 <Input
 type="number"
 min="0"
 max="100"
 value={weightCalculator.water_content}
 onChange={(e) => setWeightCalculator({ ...weightCalculator, water_content: e.target.value })}
 placeholder="80"
 />
 </div>
 <div className="space-y-2">
 <Label>Target Weight per Unit ({formData.unit_system === 'metric' ? 'g' : 'oz'})</Label>
 <Input
 type="number"
 step="0.1"
 value={weightCalculator.unit_weight}
 onChange={(e) => setWeightCalculator({ ...weightCalculator, unit_weight: e.target.value })}
 placeholder={formData.unit_system === 'metric' ? '25' : '1.0'}
 />
 <p className="text-xs text-muted-foreground">
 How much should each finished unit weigh? (e.g., {formData.unit_system === 'metric' ? '25g for snack packs' : '1 oz for small snack packs'})
 </p>
 </div>
 <div className="flex gap-2">
 <Button 
 onClick={calculateUnitsFromWeight}
 className="flex-1"
 >
 <Calculator className="w-4 h-4 mr-2" />
 Calculate Units
 </Button>
 <Button 
 variant="outline" 
 onClick={() => setShowUnitsCalculator(false)}
 className="flex-1"
 >
 Cancel
 </Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>

 {/* AI Dialog */}
 <AIEstimationDialog
 open={aiDialogOpen}
 onOpenChange={setAiDialogOpen}
 fieldLabel={aiDialogLabel}
 prompt={aiPrompt}
 setPrompt={setAiPrompt}
 onEstimate={handleAIEstimate}
 loading={aiLoading}
 />

 {/* Upgrade Dialog for Recipe Integration */}
 <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 <ChefHat className="w-5 h-5 text-primary" />
 Upgrade to Pro
 </DialogTitle>
 <DialogDescription>
 Recipe Integration is a Tier 1 Professional feature
 </DialogDescription>
 </DialogHeader>
 <div className="space-y-4">
 <div className="bg-primary/5 dark:bg-navy/20 p-4 rounded-lg">
 <p className="text-sm">
 With <strong>Pro Membership</strong>, you can:
 </p>
 <ul className="list-disc list-inside mt-2 space-y-1 text-sm text-muted-foreground">
 <li>Connect recipes to your business calculator</li>
 <li>Auto-populate ingredient costs</li>
 <li>Access advanced Recipe Engine features</li>
 <li>Use AI Scientific Assistant</li>
 <li>And much more...</li>
 </ul>
 </div>
 <div className="flex gap-2">
 <Button
 variant="default"
 className="flex-1 bg-primary hover:bg-primary/90"
 onClick={() => {
 setShowUpgradeDialog(false);
 navigate('/pricing');
 }}
 >
 View Pricing
 </Button>
 <Button
 variant="outline"
 className="flex-1"
 onClick={() => setShowUpgradeDialog(false)}
 >
 Maybe Later
 </Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>

 {/* Piece Cost Calculator Dialog */}
 <Dialog open={showPieceCostCalculator} onOpenChange={setShowPieceCostCalculator}>
 <DialogContent className="max-w-lg">
 <DialogHeader>
 <DialogTitle className="flex items-center gap-2">
 🍬 Piece-Based Ingredient Cost Calculator
 </DialogTitle>
 <DialogDescription>
 Calculate cost per piece and cost per weight unit from a bag of candy or similar items
 </DialogDescription>
 </DialogHeader>
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-2">
 <Label className="dark:text-white">Pieces in Bag</Label>
 <Input
 type="number"
 placeholder="300"
 value={pieceCostCalc.piecesInBag}
 onChange={(e) => setPieceCostCalc(prev => ({ ...prev, piecesInBag: e.target.value }))}
 />
 <p className="text-xs text-muted-foreground">e.g., 300 Jolly Ranchers</p>
 </div>
 
 <div className="space-y-2">
 <Label className="dark:text-white">Cost Per Bag ($)</Label>
 <Input
 type="number"
 step="0.01"
 placeholder="12.99"
 value={pieceCostCalc.costPerBag}
 onChange={(e) => setPieceCostCalc(prev => ({ ...prev, costPerBag: e.target.value }))}
 />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-2">
 <Label className="dark:text-white">Bag Weight</Label>
 <Input
 type="number"
 step="0.1"
 placeholder="2.5"
 value={pieceCostCalc.bagWeight}
 onChange={(e) => setPieceCostCalc(prev => ({ ...prev, bagWeight: e.target.value }))}
 />
 </div>
 
 <div className="space-y-2">
 <Label className="dark:text-white">Unit</Label>
 <select
 className="w-full h-10 px-3 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800"
 value={pieceCostCalc.bagWeightUnit}
 onChange={(e) => setPieceCostCalc(prev => ({ ...prev, bagWeightUnit: e.target.value }))}
 >
 <option value="lbs">lbs</option>
 <option value="oz">oz</option>
 <option value="kg">kg</option>
 <option value="g">g</option>
 </select>
 </div>
 </div>

 {/* Results */}
 {pieceCostCalc.costPerPiece > 0 && (
 <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg space-y-2">
 <h4 className="font-semibold text-green-900 dark:text-green-100">Results:</h4>
 <div className="space-y-1 text-sm">
 <p>
 <strong>Cost Per Piece:</strong> ${pieceCostCalc.costPerPiece}
 </p>
 <p>
 <strong>Cost Per {pieceCostCalc.bagWeightUnit}:</strong> ${pieceCostCalc.costPerUnit}
 </p>
 </div>
 <Button
 size="sm"
 className="w-full mt-2"
 onClick={() => {
 setFormData(prev => ({
 ...prev,
 ingredient_cost_per_lb: pieceCostCalc.costPerUnit,
 ingredient_cost_unit: `$/${pieceCostCalc.bagWeightUnit}`,
 ingredient_weight_unit: pieceCostCalc.bagWeightUnit
 }));
 setShowPieceCostCalculator(false);
 toast.success(`Applied: $${pieceCostCalc.costPerUnit}/${pieceCostCalc.bagWeightUnit}`);
 }}
 >
 ✅ Apply to Ingredient Cost
 </Button>
 </div>
 )}
 </div>
 </DialogContent>
 </Dialog>
 </div>
 </div>
 );
};

export default BusinessCalc;
